// 단타 알림 엔진 + 공유 스토어 — App 최상위에 항상 떠 있어, 단타 메뉴를 떠나도(다른 탭) 알림이 계속 동작.
// ── 2층 구조 ──
//   시장(전체): 🏦큰손 · ⚡국면 · 💥시장급변(속도) · 🩹반등·소진(전환) · 🔄순환매 · 🔗합류(강함)   → detectFlow + bdTick(regime)
//   종목: ✨살자리 · 🔥초입(가속) · 🚀주도주 · 🐋종목큰손 · 🔻고점반전 · 🛡️트레일 · 📌핀 손절/목표/이탈   → detectStock
// ── 원칙 ──  선행(움직임 '시작·가속'을 잡아 따라타기, 레벨·누적 아님) · 판단 태그(역행성·추격·불일치) · 성적표(울림→30/60분 채점→임계 튜닝).
// ── 폴링 ──  시장 알림(bdTick)은 앱만 열려 있으면 감지(armed 불필요). 종목/핀(scanTick)은 핀 or 🔔(armed)만. 🔔=잠금화면 푸시(앱 닫힘)용 별개.
import { useEffect, useRef, useState } from 'react';
import { fetchDaytradeBackdrop, fetchDaytradeScan, fetchDaytradeMarketEvents, fetchPushKey, postAlertLog, postPushPins, postPushPref, postPushSubscribe, postPushUnsubscribe, postPushWatch } from './api';
import { DaytradeBackdrop, DaytradeItem, FlowImpact, MarketCharacter, MarketEvent } from './types';

// 새 알림(급변/순환매/초입/주도주)을 서버 성적표에 기록(fire-and-forget) — 30/60분 뒤 서버가 채점. 15분내 중복은 서버가 무시.
function logAlert(kind: DtAlertKind, name: string, ticker: string, market: string, ref: number | null | undefined, dir: number, lag?: number | null) {
  if (ref == null) return;
  try { postAlertLog({ kind, name, ticker, market, ref, dir, regime: _lastVerdict ?? '', lag: lag ?? null }); } catch { /* noop */ }
}

// 🦵 늦지 않게 울리기 원칙(RULES #7) — 종목 알림 '늦은 정도' 범용 근사: 울림까지 레그가 이미 몇 % 진행됐나.
//   상승 기대(dir+1)=당일 저점 대비 현재가 %, 하락 기대(dir−1)=당일 고점 대비 %. high/low 없으면 |당일등락|.
function legLag(it: { price: number; high?: number; low?: number; change_pct?: number }, dir: number): number | null {
  const hi = it.high, lo = it.low, p = it.price;
  if (dir > 0 && lo && lo > 0 && p) return Math.round((p / lo - 1) * 1000) / 10;
  if (dir < 0 && hi && hi > 0 && p) return Math.round((hi / p - 1) * 1000) / 10;
  return it.change_pct != null ? Math.abs(Math.round(it.change_pct * 10) / 10) : null;
}

export type DtAlertKind = 'stop' | 'target' | 'breakdown' | 'entry' | 'flow_buy' | 'flow_sell' | 'topreversal' | 'regime' | 'trail' | 'vi' | 'leader' | 'rotation' | 'marketmove' | 'ignition' | 'reversal' | 'stockflow' | 'confluence' | 'quiet' | 'surge' | 'dayshift' | 'pricealert' | 'nearmiss' | 'floorset' | 'antflow' | 'capitulation' | 'ridefade' | 'ridegain' | 'stance' | 'flowearly' | 'shadow' | 'overnight' | 'timestop' | 'theme' | 'leaderbreak' | 'futflow' | 'obimb' | 'driveflip' | 'absorb' | 'bungee' | 'news' | 'stockmove' | 'knee' | 'lifeline' | 'prime'
  // ⏩ 90초 확인 · 🌅 아침 판정 5종(08-04 화면 투입) · 📄 공시 경고(08-12 화면 투입) · 🐋 큰손 매수 뒤 하이닉스 반응(08-15 임시 합격 → 08-22 최종 합격)
  | 'flowearly_confirmed' | 'morning_nobuy' | 'morning_dipbuy' | 'morning_inverse' | 'morning_revert' | 'expiry_kosdaq'
  // 🌙 급락일 밤 반등(08-20 화면 투입)
  | 'disclosure_warning' | 'flowreact' | 'night_rebound';
export type DtAlert = {
  id: string; time: string; name: string; ticker: string;
  kind: DtAlertKind;
  msg: string;
  drv?: number;   // 🚀 울림 순간 힘 세기 값(보드 문맥 칩 — 알림이 보드 신뢰를 등에 업게, 07-08)
  star?: boolean;   // 확신 알림(종목 신호 × 시장 배경 정렬)
  ts?: number;      // 울림 시각(ms) — 🅑 결과 꼬리표·🅓 부재중 브리핑용
  px?: number;      // 울림 시 종목 가격 — 🅑 결과 추적 기준
  dir?: number;     // 방향(+1 오름 기대/−1 내림 기대/0 중립) — 🅑 ✅/❌ 판정
  outcome?: { pct: number; min: number } | null;   // 🅑 그 후 수익률(±%)·경과분(≤60 고정)
  // 🔥 수급 데이터 끊김·복구(flow)·⚡국면 유효(regime) 꼬리표 재료 — 서버 시장 이벤트에서 옴
  mkt?: string;        // 'KOSPI'|'KOSDAQ'
  grp?: string;        // 'foreign'|'institution'|'pension'
  net?: number;        // 울림 시점 누적 순매수(억)
  fdir?: string;       // flow 방향 'buy'|'sell'(dir는 숫자라 별도)
  jk?: string;         // 🧾 알림 기록 계정(서버가 실어 보냄) — ⚡'조짐'(marketmove_fast) 검증 중 판정용
  verdict?: string;    // regime 울림 시점 국면
  ep?: string;   // 🧵 에피소드 실(서버 flow 계열 — 큰손 매수 시작→확정→데이터 끊김·복구를 한 실로)
  url?: string;  // 🗞️ news — 기사 원문 링크('원문 보기')
  kw?: string;   // 🗞️ news — 매치 키워드(신호 영향 카테고리)
  cont?: boolean;   // 🦵 knee — 같은 방향 '이어짐' 카드(방향 전환은 새 주연, 연속은 조연). 서버 push_external extra로 옴.
};
export const ALERT_KIND: Record<DtAlertKind, { cls: string; icon: string; label: string }> = {
  stop:        { cls: 'text-red-300',    icon: '📉', label: '손절 경보' },
  target:      { cls: 'text-green-300',  icon: '🎯', label: '목표 도달' },
  breakdown:   { cls: 'text-amber-300',  icon: '⚠️', label: '흐름 약화' },
  entry:       { cls: 'text-indigo-300', icon: '✨', label: '살 자리' },
  flow_buy:    { cls: 'text-red-300',    icon: '🏦', label: '큰손 매수' },
  flow_sell:   { cls: 'text-blue-300',   icon: '🌊', label: '큰손 매도' },
  topreversal: { cls: 'text-orange-300', icon: '🔻', label: '고점 반전' },
  regime:      { cls: 'text-purple-300', icon: '⚡', label: '국면 전환' },
  trail:       { cls: 'text-green-300',  icon: '🛡️', label: '이익 보호' },
  vi:          { cls: 'text-amber-300',  icon: '🚨', label: 'VI 발동' },
  leader:      { cls: 'text-pink-300',   icon: '🚀', label: '강세 주도주' },
  rotation:    { cls: 'text-amber-300',  icon: '🔄', label: '순환매' },
  marketmove:  { cls: 'text-rose-300',   icon: '⚡', label: '지수 급락 속보' },
  ignition:    { cls: 'text-orange-400',  icon: '🔥', label: '급등락 초입' },
  reversal:    { cls: 'text-teal-300',    icon: '🩹', label: '반등·소진' },
  stockflow:   { cls: 'text-cyan-300',    icon: '🐋', label: '종목 큰손' },
  confluence:  { cls: 'text-red-400',     icon: '🔗', label: '합류(강함)' },
  quiet:       { cls: 'text-sky-300',     icon: '🤫', label: '이상하게 조용함' },
  surge:       { cls: 'text-fuchsia-300', icon: '🎢', label: '재가속' },
  dayshift:    { cls: 'text-violet-300',  icon: '🌗', label: '장 성격 전환' },
  pricealert:  { cls: 'text-emerald-300', icon: '🔔', label: '내 가격 알림' },
  nearmiss:    { cls: 'text-amber-200',   icon: '🌡️', label: '아깝게 빗나감' },
  floorset:    { cls: 'text-lime-300',    icon: '🧯', label: '매도 소진' },
  antflow:     { cls: 'text-yellow-300',  icon: '🐜', label: '개인 수급' },
  capitulation:{ cls: 'text-lime-300',    icon: '🤝', label: '개인 투항' },
  ridefade:    { cls: 'text-orange-300',  icon: '🎯', label: '탄 수급 소진' },
  ridegain:    { cls: 'text-red-300',      icon: '🎯', label: '탄 수급 증축' },
  stance:      { cls: 'text-orange-300',   icon: '🧭', label: '수급 구도' },
  flowearly:   { cls: 'text-amber-300',    icon: '⏩', label: '큰손 매수·매도 시작' },
  shadow:      { cls: 'text-orange-200',   icon: '🕯️', label: '큰손 의심(미확정)' },
  overnight:   { cls: 'text-indigo-300',   icon: '🌙', label: '밤새 보유 판단' },
  timestop:    { cls: 'text-slate-300',    icon: '⏳', label: '시간 손절' },
  theme:       { cls: 'text-teal-300',     icon: '🌊', label: '테마 동반' },
  leaderbreak: { cls: 'text-rose-300',     icon: '🏛️', label: '주도주 이탈(잠정)' },
  futflow:     { cls: 'text-purple-300',   icon: '🔮', label: '외인 선물(잠정)' },
  obimb:       { cls: 'text-amber-200',    icon: '⚖️', label: '호가쏠림(잠정)' },
  driveflip:   { cls: 'text-rose-300',     icon: '🚀', label: '힘 세기 전환(잠정)' },
  absorb:      { cls: 'text-teal-300',     icon: '🧲', label: '투매 흡수(잠정)' },
  bungee:      { cls: 'text-orange-300',   icon: '🪤', label: '가짜 반등 의심(잠정)' },
  news:        { cls: 'text-amber-300',    icon: '🗞️', label: '뉴스 속보' },
  stockmove:   { cls: 'text-rose-300',     icon: '⚡', label: '하이닉스 급변' },
  knee:        { cls: 'text-rose-300',     icon: '⚡', label: '하이닉스 힘 감지' },
  prime:       { cls: 'text-amber-200',    icon: '🎖️', label: '종합 신호' },
  lifeline:    { cls: 'text-amber-300',    icon: '🔌', label: '데이터 끊김·복구' },
  flowearly_confirmed: { cls: 'text-amber-300', icon: '⏩', label: '큰손 매수 시작(90초 확인)' },
  morning_nobuy:   { cls: 'text-rose-300',    icon: '🌅', label: '아침 판정 · 추격 금지' },
  morning_dipbuy:  { cls: 'text-emerald-300', icon: '🌅', label: '아침 판정 · 매수 후보' },
  morning_inverse: { cls: 'text-blue-300',    icon: '🌅', label: '아침 판정 · 인버스 우위' },
  morning_revert:  { cls: 'text-emerald-300', icon: '🌅', label: '아침 판정 · 되돌림 후보' },
  expiry_kosdaq:   { cls: 'text-teal-300',    icon: '📅', label: '코스닥 만기 전날' },
  disclosure_warning: { cls: 'text-blue-300', icon: '📄', label: '공시 경고' },
  flowreact:   { cls: 'text-red-300',      icon: '🐋', label: '큰손 매수 뒤 하이닉스 반응' },   // 📖 TERMS.md 1:1 표 그대로(08-22 최종 합격)
  night_rebound: { cls: 'text-indigo-300', icon: '🌙', label: '급락일 밤 반등' },   // 📖 TERMS.md 1:1 표 그대로(08-20 화면 투입)
};
// 📖 알림 사전(호버 툴팁) — 각 감지기가 '무엇을 보고 언제 우는지' 한 줄 설명. ⚙ 칩 title로 노출(07-07 요청).
export const KIND_DESC: Record<DtAlertKind, string> = {
  stop:        '보유 표시한 종목이 손절 기준선을 깨고 내려감 — 지금 끊을지 결정할 시간이라는 신호예요.',
  target:      '보유 종목이 목표 수익 구간에 도달 — 익절(이익 실현)을 검토하라는 신호예요.',
  breakdown:   '보유 종목의 상승 흐름이 약해짐(기준선 이탈 등) — 이익을 지킬지 점검하라는 경고예요.',
  entry:       '눌림목 등 매수 후보 자리가 나온 종목 — ★ 붙은 것만 검증된 자리(소리)이고 나머지는 참고예요.',
  flow_buy:    '외국인·기관·연기금이 시장 전체를 큰 규모로 사기 시작(흐름 확정) — 시장 방향의 몸통 신호예요. ⚠️ 이 수급 숫자는 한국거래소(KRX) 정규장 집계 기준이라, 대체거래소(NXT)에서 오간 물량은 어떤 창구에도 안 잡혀요.',
  flow_sell:   '외국인·기관·연기금이 시장 전체를 큰 규모로 팔기 시작(흐름 확정) — 하락 압력의 몸통 신호예요. ⚠️ 이 수급 숫자는 한국거래소(KRX) 정규장 집계 기준이라, 대체거래소(NXT)에서 오간 물량은 어떤 창구에도 안 잡혀요.',
  topreversal: '오르던 종목이 고점을 찍고 꺾이는 패턴 — 따라 사면 위험, 나올 때를 보라는 신호예요.',
  regime:      '오늘 장 전체가 "사기 좋은 배경 ↔ 불리한 배경"으로 전환 — 가장 위층의 배경 신호예요.',
  trail:       '이익 난 보유 종목이 정점 대비 크게 되돌림 — 남은 이익을 보호하라는 신호예요.',
  vi:          '변동성완화장치(VI) 발동 — 가격이 급변해 단일가 매매로 잠시 전환된 종목이에요.',
  leader:      '오늘 시장을 끌고 가는 강세 주도주로 포착된 종목이에요.',
  rotation:    '외국인 자금이 코스피↔코스닥 사이를 갈아타는 순환매 감지 — 어느 시장이 유리해지는지 참고해요.',
  marketmove:  '지수가 몇 분 안에 급락/급등으로 가속 — 레버리지·인버스 타이밍 참고용 속보예요.',
  ignition:    '거래대금이 평소의 몇 배로 튀며 가격이 막 움직이기 시작 — 급등락 레그의 초입 후보예요.',
  reversal:    '지수가 바닥에서 되돌리기 시작(반등) 또는 상승 힘이 꺾임(소진) — 전환점 후보 신호예요.',
  stockflow:   '특정 종목에 기관·외국인 추정 자금이 크게 유입/이탈 중이라는 신호예요. 성적 부진으로 소리 없이 피드에만 떠요(성적 회복 시 복권). ⚠️ 한국거래소(KRX) 정규장 집계 기준이라 대체거래소(NXT) 거래분은 빠져 있어요.',
  confluence:  '90초 안에 같은 방향의 시장 신호가 2개 이상 정렬 — 겹칠수록 강한 확신 신호(★)예요.',
  quiet:       '감시 종목의 거래가 마르고 변동이 죽음 — 이상하게 조용한 상태일 수 있어 지켜보라는 신호예요.',
  surge:       '움직이던 종목이 쉬었다가 다시 가속 — 두 번째 파도(재점화) 후보예요. 시장 전체 급락일(코스피 -2.5% 아래)엔 감시(👁) 종목만 알려요(온 종목이 울리는 스팸 방지).',
  dayshift:    '오전과 오후의 장 성격이 바뀜(롱 유리↔불리) — 전략을 바꿀 타이밍 참고예요.',
  pricealert:  '내가 직접 설정한 가격에 도달했다는 알림이에요.',
  nearmiss:    '신호 조건 직전까지 갔다가 아깝게 빗나간 종목 — 곧 익을지 몰라 재도전을 감시해요.',
  floorset:    '종목의 매도 물량이 소진되며 바닥을 다지는 모습 — 반등 준비 후보예요. 시장 전체 급락일엔 자동으로 쉬어요(떨어지는 칼날에 바닥 신호가 잘 틀려서).',
  antflow:     '개인(개미) 자금의 쏠림 — 보통 큰손과 반대로 움직여 역지표로 참고해요.',
  capitulation:'버티던 개인들이 견디다 못해 던지는 투항성 매도 — 역설적으로 바닥 근처 신호 후보예요. 시장 전체 급락일엔 자동으로 쉬어요(패닉장에선 바닥 신호가 잘 틀려서).',
  ridefade:    '"탔어요" 표시한 큰손 흐름의 동력이 식음 — 출구를 검토하라는 신호예요.',
  ridegain:    '탄 큰손 흐름이 더 강해짐(추가 매집/투매) — 계속 들고 갈 근거가 되는 신호예요.',
  stance:      '외인·기관·개미 조합과 가격으로 본 오늘 이 종목의 판 구도(쌍끌이·물량받이 등)예요. 시장 전체 급락일엔 자동으로 쉬어요(판이 다 무너지는 날엔 구도 판별이 잘 틀려서).',
  flowearly:   '큰손 매매가 확정되기 전, 빠른 속도로 시작되는 걸 먼저 감지 — 큰손 알림의 예고편(잠정)이에요. 지금은 90초 확인판이 화면을 맡고 있어 이 원본은 비교용으로만 채점해요.',
  shadow:      '확정 데이터가 나오기 전, 정황상 큰손 개입이 의심되는 가장 이른 그림자 신호(잠정)예요.',
  overnight:   '장 마감 전, 보유 종목을 하룻밤 들고 갈지 점검하라는 리마인더예요.',
  timestop:    '산 지 오래됐는데 가지 않는 보유 종목 — 시간 손절(기회비용)을 검토하라는 신호예요.',
  theme:       '같은 테마 종목들이 동시에 움직임 — 개별 종목이 아니라 테마 파도라는 뜻이에요.',
  leaderbreak: '삼성전자 등 대형주 묶음이 지수보다 먼저 이탈/반등 — 지수의 선행 신호 후보(잠정)예요.',
  futflow:     '외국인이 선물을 대량으로 사기/팔기 시작 — 선물은 지수를 끌고 가는 선행 신호(잠정)예요.',
  absorb:      '폭락 다음날 아침, 개인이 반대매매 등으로 쏟아내는 물량을 외국인·기관·프로그램이 받아갈 때 울려요 — 바닥 다지기 후보(잠정, 하루 1회).',
  bungee:      '급락 중 지수가 저점에서 반짝 튈 때 울려요 — 실측상 패닉장 반등의 76%가 30분 내 도로 무너졌어요(큰손 미동반이면 86%). 반등 추격 매수 주의(잠정).',
  obimb:       '지수 ETF 호가에서 사자/팔자 대기물량이 한쪽으로 2배 이상 쏠림 — 가격이 움직이기 직전의 압력(잠정)이에요.',
  driveflip:   '🚀 힘 세기(지금 차트를 미는 힘)가 방향을 바꿔 1분 넘게 유지되거나, ±40 강함 구간에 들어섬 — 분 단위 흐름의 변곡 후보예요(잠정).',
  news:        '연기금 리밸런싱·반도체 대형(하이닉스·HBM 등)·공매도/관세처럼 신호의 의미를 바꾸는 뉴스가 나오면 제목 그대로 바로 띄워요(해석 없음). 따로 찾아보다 늦지 않게 — 기본으로 폰 잠금화면까지 알려요.',
  stockmove:   'SK하이닉스 체결가가 최근 2분 새 ±0.7% 넘게 급하게 움직였을 때를 기록해요 — 07-24부터는 화면·소리 없이 검증 중으로 기록만 하며 하이닉스 힘 감지 신호의 대조군으로 써요(사용자 판정: 가격만 보는 건 도움이 약함).',
  lifeline:    '서버가 받아오는 데이터(체결 시세·수급·지수 시세)가 장중에 끊기면 "끊김", 다시 들어오면 "복구"를 한 번씩 알려요 — 알림이 안 오는 게 시장이 조용해서인지, 데이터가 죽어서인지 구분하려고요. 거래일 장중(09:05~)에만 보고, 체결 시세는 동시호가 전(15:20)에 감시를 내려요. 체결 시세 끊김은 서버 문제일 수도, 거래소가 멈춘 것(서킷브레이커 등)일 수도 있어요. 한계 — 바깥에서 오는 데이터가 끊겼는지를 보는 감시라, 서버 자신이 죽으면 감시도 같이 죽어서 알려드릴 수 없어요.',
  prime:       'SK하이닉스에서 여러 근거(호가 벽·체결 쏠림·선물·큰손 수급·시간대·장세·뉴스)가 **한 방향으로 겹치는 순간**만 골라 울려요 — 자주 울리지 않는 대신 확신이 높을 때만 나옵니다. 알림에 무엇이 겹쳤는지 함께 적어드려요. ⚠️ 시험 중(성적 쌓는 중)이라 참고용이고, 잠금화면 푸시는 보내지 않아요(화면·PC 소리만).',
  flowearly_confirmed: '큰손이 빠르게 사들이기 시작한 뒤 **90초가 지나도 같은 방향이 이어졌을 때만** 울려요 — 확인 전 원본을 한 번 걸러낸 판이라 조금 늦는 대신 더 정확해요. 이어지지 않으면 아무 말도 하지 않아요. **매도 쪽은 화면에 오지 않아요**(성적이 모자라 검증 중으로 채점만). 시험 중(성적 쌓는 중)이라 참고용이고, 폰 잠금화면 알림은 보내지 않아요.',
  morning_nobuy:   '개장 전(아침 8시 50분)에 판정해요. 간밤 나스닥이 크게 올랐으면 "오늘은 하이닉스를 따라 사지 말자"고 알려드려요 — 미국이 크게 오른 다음날은 되돌리는 쪽이 잦았거든요(지난 기록 10번 중 7번). 시험 중이에요.',
  morning_dipbuy:  '개장 전(아침 8시 50분)에 판정해요. 간밤 미국 반도체와 엔비디아가 함께 폭락했으면 "오늘 하이닉스는 아침 매수 후보"라고 알려드려요(지난 기록 10번 중 6.1번). 시험 중이에요.',
  morning_inverse: '개장 전(아침 8시 50분)에 판정해요. 어젯밤 미국 금리 결정(FOMC)이 있었으면 "오늘은 인버스가 유리한 날"이라고 알려드려요 — 그 다음날 코스피는 장중에 흘러내리는 쪽이 잦았어요(10번 중 6.1번). 시험 중이에요.',
  morning_revert:  '개장 전(아침 8시 50분)에 판정해요. 어제 하이닉스가 2% 넘게 급락했고 장이 평소만큼만 출렁이는 국면이면 "되돌림 매수 후보"라고 알려드려요(10번 중 6번). 요즘처럼 장이 아주 크게 출렁이는 국면에서는 **일부러 아무 말도 안 해요**(그럴 땐 잘 안 맞아서). 시험 중이에요.',
  expiry_kosdaq:   '옵션 만기(매월 둘째 목요일) **전날 오후 3시**에 한 번 알려드려요 — 전날 종가에 사서 만기 종가에 파는 구간이 지난 기록에선 오르며 끝나는 쪽이 잦았어요(10번 중 6.6번). 시험 중이에요.',
  disclosure_warning: '내가 가진 종목(📌핀·👁감시·내 현황 탭 보유)에 주가를 누르는 공시 세 가지 — 전환사채 발행 · 유상증자 · 자사주 처분 — 가 뜨면 알려드려요. 2022년 이후 기록으로 이런 공시 뒤 20거래일은 10번 중 5.8~6.7번 내렸어요(한가운데 성적 −2.7~−7.1%). 밤에 뜬 공시라도 다음 날 시가까지 벌어진 틈이 거의 0이라 **아침에 정리해도 통계상 늦지 않아요** — 그래서 장 마감 뒤 공시는 다음 날 아침 8시 35분에 몰아서 알려드리고, 장중 공시는 30분마다 확인해요. 카드에 공시 원문 링크가 함께 붙어요. 잠정(실전 성적을 쌓는 중)이고, 폰 잠금화면 알림은 보내지 않아요.',
  flowreact:   '외국인·기관·연기금이 코스피를 크게 사기 시작한 직후, 5분 안에 SK하이닉스가 실제로 +0.2% 따라 오르면 그 순간 알려드려요 — "큰손이 사니까 반도체가 진짜 움직인다"를 확인한 자리예요. 지금까지 이런 순간은 **5분 뒤에도 10번 중 8.6번 이어졌어요**(표본 35건). ⚠️ 다만 이 알림은 하이닉스가 **이미 +0.2% 오른 걸 보고** 우는 구조라 그만큼은 늦어요 — 그 몫과 사고팔 때 드는 비용까지 다 빼도 10번 중 7.3번은 이어졌습니다. 성적이 기준(10번 중 7번)을 넘겨 **합격한 알림**이에요(2026-08-22). 성적은 그 뒤로도 계속 채점하고, 폰 잠금화면 알림은 보내지 않아요.',
  night_rebound: '거래일 **오후 3시 20분**(마감 직전)에 한 번만 봐요. 오늘 코스피가 **2% 넘게 빠졌고** 내일이 쉬는 날이 아니면 "오늘 밤은 통계상 반등이 잘 나온 밤"이라고 알려드려요 — 2001년부터 26년을 전부 세어 보니 그런 밤 290번 중 **10번에 6.6번**은 다음 날 아침 시가가 어제 종가보다 높았어요(평균 +0.33%). ⚠️ **안심하라는 말이 아니에요** — 빗나갈 땐 더 크게 빠집니다(나쁜 밤 상위 5% 평균 −3.9%, 가장 나빴던 밤 −6.1%). 금요일 밤과 연휴 전날 밤은 성적이 나빠서 **일부러 아무 말도 안 해요**. 시험 중이고, 폰 잠금화면 알림은 보내지 않아요(화면·PC 소리만).',
  knee:        'SK하이닉스의 주문 압력(호가)과 실제 체결이 같은 방향으로 평소보다 세게 쏠릴 때 울려요 — 매수/매도 힘이 실리는 초입 후보예요(레버리지·인버스 참고). 같은 방향이 이어지는 동안엔 카드 한 장이 제자리에서 갱신되고(지금 몇 분째·몇 번째·힘 몇 배), 방향이 바뀌면 새 카드로 알려드려요. 시험 중 신호(잠정 기준값, 며칠 표본으로 다듬는 중).',
};

// 알림 종류 한글 라벨로 on/off UI 구성용(피드 분류와 별개)
export const ALERT_KINDS_ORDER: DtAlertKind[] = ['ignition', 'surge', 'quiet', 'nearmiss', 'floorset', 'capitulation', 'shadow', 'ridefade', 'ridegain', 'stance', 'stockflow', 'antflow', 'entry', 'leader', 'topreversal', 'pricealert', 'overnight', 'stop', 'target', 'breakdown', 'trail', 'timestop', 'confluence', 'reversal', 'marketmove', 'stockmove', 'knee', 'prime', 'rotation', 'theme', 'leaderbreak', 'futflow', 'absorb', 'bungee', 'obimb', 'driveflip', 'flowearly_confirmed', 'flowearly', 'flow_buy', 'flow_sell', 'regime', 'dayshift',
  'morning_nobuy', 'morning_dipbuy', 'morning_inverse', 'morning_revert', 'expiry_kosdaq',   // 🌅 아침 판정(08-04 투입)
  'disclosure_warning',   // 📄 공시 경고(08-12 투입)
  'flowreact',            // 🐋 큰손 매수 뒤 하이닉스 반응(08-22 최종 합격)
  'night_rebound',        // 🌙 급락일 밤 반등(08-20 투입)
  'news', 'lifeline', 'vi'];
const _DEFAULT_OFF: DtAlertKind[] = ['vi'];   // VI는 변동성 커 기본 OFF(원하면 켜기)
export function isKindEnabled(kind: DtAlertKind): boolean {
  try {
    const v = localStorage.getItem('dt_off_' + kind);
    if (v === '1') return false;
    if (v === '0') return true;
    return !_DEFAULT_OFF.includes(kind);   // 미설정이면 기본값
  } catch { return !_DEFAULT_OFF.includes(kind); }
}
export function setKindEnabled(kind: DtAlertKind, on: boolean) {
  try { localStorage.setItem('dt_off_' + kind, on ? '0' : '1'); } catch { /* noop */ }
}
// ① 알림 2층제 — 감지(피드에 다 뜸)와 통보(소리·푸시)를 분리. '행동층'만 기본 소리 켜짐:
//   내 포지션(손절·목표·트레일·시간손절·오버나잇·흐름약화) + 검증된 진입(★살자리·다지기 매도소진) + 시장 브레이크(국면전환·합류).
//   나머지 24종(큰손·큰손 매수 시작·그림자·급등초입·구도·수급…)은 '맥락층' = 피드만·소리 꺼짐. ⚙에서 🔔로 개별 승격/강등.
export const ACTION_KINDS: DtAlertKind[] = ['stop', 'target', 'trail', 'timestop', 'overnight', 'breakdown', 'floorset', 'entry', 'regime', 'confluence', 'flow_buy', 'flow_sell', 'marketmove', 'reversal', 'lifeline'];   // ① 07-07 급변·반등 소리 켜짐 승격(레버리지·인버스 매매의 주 신호). 🔌 lifeline(07-25)=데이터가 죽었다는 시스템 경보라 성적과 무관하게 행동층(카드 흐리게 처리 금지·⚙ 🔊 그룹)
const _ACTION_SET = new Set<DtAlertKind>(ACTION_KINDS);
// ③ 알림 소리 켜짐 판정 제도 — 소리 켜짐 권한을 취향이 아니라 '성적표'가 결정. 기대분포(dt_exp_cache, DayTradePage가 적재)로 판정:
//   표본≥30 & 승률≥50 → 소리 켜짐(승격) · <38 → 강등(소리 꺼짐) · 그 사이 → 소리 꺼짐. 표본 부족이면 기본 레이어(행동층=소리 켜짐).
//   #10 자동볼륨(성적 낮으면 소리 꺼짐)을 이 소리 켜짐 판정으로 통합 — 좋은 감지기는 실력으로 소리 켜짐 권한을 '벌고', 나쁜 건 조용히 강등.
export type KindLicense = '발성' | '수습' | '강등';
// 🧭 조건부 소리 켜짐 판정 컨텍스트(메타라벨링-lite) — 지금 장 오늘 장 성격·선물 방향. DayTradePage가 backdrop 도착 시 갱신.
//   같은 알림도 '이 국면에서의 승률'이 표본 30+이면 그걸로 소리 켜짐 판정(예보관 성적을 계절별로 따짐).
let _licCtx: { ch?: string | null; fut?: number | null } = {};
export function setLicenseCtx(ch?: string | null, fut?: number | null) { _licCtx = { ch, fut }; }
type _ExpEnt = { n: number; win: number; lag?: number | null; mfe?: number | null; by_char?: Record<string, { n: number; win: number }>; by_fut?: Record<string, { n: number; win: number }> };
function _scoreLicense(kind: DtAlertKind): KindLicense | null {
  try {
    const exp = JSON.parse(localStorage.getItem('dt_exp_cache') || '{}') as Record<string, _ExpEnt>;
    let best: _ExpEnt | null = null;
    for (const d of ['1', '-1']) { const e = exp[kind + '|' + d]; if (e && (!best || e.n > best.n)) best = e; }
    if (best && best.n >= 30) {
      // 🦵 늦지 않게 울리기 원칙(RULES #7 ⓒ) — 평균 늦은 정도 ≥ 평균 최대 이익 폭(MFE)이면 "맞긴 맞는데 늦게 맞는 알림" → 승률 무관 강등
      if (best.lag != null && best.mfe != null && best.mfe > 0 && best.lag >= best.mfe) return '강등';
      let win = best.win;
      // 조건부 우선 — 현재 국면 표본 30+이면 국면 승률로(둘 다 있으면 표본 큰 쪽), 아니면 누적 승률.
      const cands: { n: number; win: number }[] = [];
      const bc = _licCtx.ch && best.by_char?.[_licCtx.ch]; if (bc && bc.n >= 30) cands.push(bc);
      const bf = _licCtx.fut != null && best.by_fut?.[String(_licCtx.fut)]; if (bf && bf.n >= 30) cands.push(bf);
      if (cands.length) win = cands.sort((a, b) => b.n - a.n)[0].win;
      return win >= 50 ? '발성' : win < 38 ? '강등' : '수습';
    }
  } catch { /* noop */ }
  return null;   // 표본 부족 → 기본 레이어로
}
export function kindLicense(kind: DtAlertKind): KindLicense {
  try {
    const v = localStorage.getItem('dt_sound_' + kind);
    if (v === '1') return '발성';   // 사용자 수동 승격(최우선)
    if (v === '0') return '강등';   // 사용자 수동 강등
  } catch { /* noop */ }
  // 🔊 소리 결정은 여기(성적 소리 켜짐 판정)서 하지 않는다(07-22) — 화면 알림 '소리'는 shouldSoundAlert가 '화면 알림+수동토글'로만
  //   판정하도록 분리했다(잠깐 있었던 news·marketmove '소리 켜짐' 고정 특례도 그 일반 수리에 흡수). kindLicense/_scoreLicense는
  //   이제 소리에 관여하지 않고 통계탭·sevOf 표시용으로만 남는다.
  return _scoreLicense(kind) ?? (_ACTION_SET.has(kind) ? '발성' : '수습');
}
export function isSoundKind(kind: DtAlertKind): boolean {
  return kindLicense(kind) === '발성';
}
export function setSoundKind(kind: DtAlertKind, on: boolean) {
  try { localStorage.setItem('dt_sound_' + kind, on ? '1' : '0'); } catch { /* noop */ }
}
// 🔊 화면 알림 사운드 판정(07-22 전면 수리) — 'PC 소리가 유일 채널'인 사용자에게 화면 알림에서 소리가 안 나던 사고를 막는다.
//   근본 원인: v3에서 '화면 판정'은 isSurvivorFeed(화면 알림만)로 갈았는데 '소리 판정'은 옛 성적 소리 켜짐 판정 제도(_scoreLicense)가
//   그대로 지키고 있었다 → dt_exp_cache(옛 30/60분 자로 채점한 성적)가 n≥30을 넘긴 종류부터 하나씩 '강등'(소리 꺼짐)됐고,
//   특히 늦지 않게 울리기 원칙(평균늦은 정도≥평균최대 이익 폭 → 무조건 강등)이 수급 지연이 실측된 큰손 알림(연기금 3분+·청크 ~95초)을 때려
//   flow_sell이 ACTION_KINDS 기본 '소리 켜짐'인데도 소리가 꺼졌다. 큰손 매수 시작(flowearly)·가격알람(pricealert)은 아예 ACTION_KINDS
//   명단에 없어 처음부터 소리 없는 설계. 결과: 09:22 코스닥 급락 경보·기관 매도 급증이 피드엔 떴는데 PC에서 소리가 안 났다.
//   수리(v3 절대 규칙 정합 "승격·강등은 통계가 아니라 사용자 말로만") — 소리 결정에서 성적(_scoreLicense)은 완전히 손 뗀다.
//   fresh는 이미 화면 알림만 통과(:402)하므로, 소리는 '화면 알림이면 소리 켜짐, ⚙에서 수동으로 끈 것(dt_sound_<kind>='0')만 침묵'.
//   (옛 성적 판정 장치 자체는 소리 없이 통계탭 표시용으로만 보존.)
export function shouldSoundAlert(a: DtAlert): boolean {
  if (!isSurvivorFeed(a)) return false;   // 화면에 뜨는 화면 알림만 소리(:402 fresh와 동일 판정)
  try { if (localStorage.getItem('dt_sound_' + a.kind) === '0') return false; } catch { /* noop */ }
  return true;
}
const _topFired: Record<string, number> = {};   // 고점 반전 알림 쿨다운(종목 → 마지막 발생 ts)
const _trailFired: Record<string, number> = {};  // 트레일링(이익 보호) 알림 쿨다운
const _pinSince: Record<string, number> = {};     // #2 시간손절 — 핀(보유) 시작 추정 시각(첫 관측)
const _timestopFired: Record<string, number> = {}; // #2 시간손절 알림 쿨다운
const _leaderFired: Record<string, number> = {}; // 강세 주도주 알림 쿨다운
const _igniteFired: Record<string, number> = {};  // 🔥 급등/급락 초입 알림 쿨다운
const _stockFlowBase: Record<string, number> = {}; // 🐋 종목 큰손 — 종목|주체 → 마지막 울림 시점 누적(억). 증분(현 누적−기준선)이 임계 돌파 시 울림 후 기준선 이동(중복 구조적 차단).
// 🤫 침묵 감지 — 폭발 직전 '이상한 고요'(소란 뒤 정적+물밑 매집). 2단: 장전(🌀 배지) → 격발(첫 미동 푸시).
const _quietHist: Record<string, { t: number; price: number; amt: number; sup: number }[]> = {};   // 종목별 최근(≤12분) 시계열
const _quietArmed: Record<string, { t: number; name: string }> = {};   // 🌀 장전(이상하게 조용함) — 정적 성립, 격발 대기
const _quietFired: Record<string, number> = {};                        // 🤫 격발 쿨다운(종목 → 마지막 울림 ts)
// ❷ 급등초입 속도바 정규화 — 고정 '30초 0.5%'는 250만원 초대형주엔 비현실적(4분 +1.7%가 침묵).
//   _quietHist(≤12분 시계열)로 그 종목 '평상시 30초 이동폭 중앙값'을 재고, 그 4배를 이상 가속 바로. 캡0.5(현행보다 안 쉬워짐)·바닥0.22.
function _veloBar(tk: string): number {
  const h = _quietHist[tk] || [];
  if (h.length < 8) return 0.5;   // 데이터 부족 → 현행 바
  const moves: number[] = [];
  for (let i = 1; i < h.length; i++) {
    const dtSec = (h[i].t - h[i - 1].t) / 1000;
    if (dtSec > 5) moves.push(Math.abs(h[i].price / h[i - 1].price - 1) * 100 * (30 / dtSec));   // 30초 정규화
  }
  if (moves.length < 6) return 0.5;
  const med = moves.sort((a, b) => a - b)[Math.floor(moves.length / 2)];
  return Math.min(0.5, Math.max(0.22, med * 3));   // 평상시의 3배 = 이상 가속(기록 재생 튜닝: SK 궤적 바 0.30 < 피크 0.32 → 포착. 품질은 strength·dStr 4중AND가 보증)
}
// 🎢 재가속 — |당일|≥2.5% 종목이 최근 5분±1.5%/10분±2.0%(감시/핀은 1.0%/1.5%) 다시 빠르게 움직이고 진행 중일 때. 🔥초입과 상호배타.
const _surgeFired: Record<string, number> = {};    // 종목|방향 → 마지막 울림 ts(쿨다운 10분)
const _surgeAnchor: Record<string, number> = {};   // 종목|방향 → 마지막 울림 가격(새 다리 판정: 기준선 대비 +1.5%↑)
const _surgeCount: Record<string, number> = {};    // 종목|방향 → 당일 울림 횟수(상한 3, 폭락장 스팸 차단)
const _viSeen = new Set<string>();                // 오늘 이미 VI 알린 종목
let _viSeenDay = '';                              // 날짜 바뀌면 비움 — 밤새 켜둔(armed) 탭에서 다음날 VI 침묵 방지(07-07 전수감사)
let _viInit = false;                              // VI 첫 폴링 여부(기존 발동분은 알림 제외)
const _nearFired: Record<string, number> = {};   // 🌡️ 아깝게 빗나감(지금 살 만함 직전) 쿨다운(종목 → 마지막 ts)
const _entryTs: Record<string, number> = {};      // #5 재진입 — 종목별 마지막 '살 자리' 울림 시각(두 번째 눌림 감지용)
// 💤 07-22 ② 재움(v3) — 폭주 다이제스트 상태 기계. addAlerts엔 이제 화면 알림만 오고(전부 울려야 함) 폭주 억제를
//   걷어냈다(addAlerts 참조). 되살리려면 아래 3줄 + _flushDigest + addAlerts의 다이제스트 블록을 함께 복원.
// const _burstTimes: number[] = [];
// let _digestPending: DtAlert[] = [];
// let _digestLastFlush = 0;
// const _BURST_N = 12, _BURST_WIN = 60000, _DIGEST_INTERVAL = 5 * 60 * 1000;
const _floorFired: Record<string, number> = {};   // 🧯 매도 소진(바닥 다지기 진입) 쿨다운(종목 → 마지막 ts)
const _capFired: Record<string, number> = {};      // 🤝 개미 투항(개미 던지고 큰손 받기) 쿨다운(종목 → 마지막 ts)
const _stockFlowFired: Record<string, number> = {}; // 🐋 종목 큰손 종목+주체별 쿨다운(대형주 스팸 억제, 대량은 관통)
const _stanceFired: Record<string, number> = {};    // 🧭 수급 구도 경보 쿨다운(종목 → 마지막 ts)
let _lastVerdict: string | null = null;          // 최신 시장 배경(확신 알림용 — 매 틱 현재 판정)
let _lastCharacter: MarketCharacter | null = null;   // 🅐 최신 장 성격(매수형 알림 게이트 + 전환 감지용)
let _kospiNow: number | null = null;             // 🔪 오늘 장 성격 거부권용 최신 코스피 등락률(패닉일 바닥류 침묵, 07-08 하루 복기)
let _driveNow: number | null = null;             // 🚀 최신 힘 세기 값(알림에 보드 문맥 칩 스탬프용)
// 큰손 스윙·순환매·국면·장성격 '전환 감지' 상태는 서버(market_events)로 이관 — 관련 상태/영속 헬퍼 제거됨.

type PinPlan = { entry: number; stop: number; target: number; owned?: boolean };   // ❹ owned=false면 '관찰'(미보유·계획만) — 보유성 알림 억제·진입성 복원. 기본 보유.

function rowGrade(it: DaytradeItem): string {
  return it.live ? (it.pullback_grade ?? '부적합') : (it.change_pct >= 29 ? '상한가' : '대기');
}
function fmtEok(v: number): string {
  const a = Math.abs(v), s = v > 0 ? '+' : v < 0 ? '−' : '';
  if (a >= 10000) return `${s}${(a / 10000).toFixed(1)}조`;
  if (a >= 1) return `${s}${Math.round(a).toLocaleString('ko-KR')}억`;
  return '0';
}

// 🎨 v4 피드 문구 표준(07-21) — 한 알림 = 「헤드라인\n본문 문장\n보조 줄」. 축약·은어·이모지 약어 금지.
//   헤드라인 = 5단어 이내 '누가/뭐가 + 무슨 일'(이모지·숫자 없음, 카테고리 아이콘은 렌더가 앞에 붙임).
//   본문 = 완전한 존댓말 문장 1개(핵심 숫자 2개까지 — 이번에 얼마나 + 오늘 누적).
//   보조 = 부가 수급을 풀네임으로(외국인 …·기관 …·개인 …). 렌더가 시각을 보조 줄에 함께 표시.
export function layerMsg(head: string, body: string, aux?: string): string {
  return aux ? `${head}\n${body}\n${aux}` : `${head}\n${body}`;
}
export function splitAlert(msg: string): { head: string; body: string; aux: string } {
  const parts = (msg || '').split('\n');
  // 옛 포맷(개행 없는 한 줄)은 통째로 헤드라인 취급 — 전환기 안전망.
  return { head: (parts[0] || '').trim(), body: (parts[1] || '').trim(), aux: (parts.slice(2).join(' · ') || '').trim() };
}
// 보조 줄(부가 수급) — 풀네임. 데이터 없으면 빈 문자열(보조 줄 생략).
function auxFlow(it: DaytradeItem): string {
  if (it.foreign_est == null || it.inst_est == null) return '';
  const r = it.retail_est ?? -(it.foreign_est + it.inst_est);
  return `외국인 ${fmtEok(it.foreign_est)} · 기관 ${fmtEok(it.inst_est)} · 개인 ${fmtEok(r)}`;
}

// 🧭 수급 구도 — 외인·기관·개미(억)와 가격 방향을 합쳐 '지금 누가 이기는 판인지' 한 장으로 판정.
//   조각 알림(주체별)만으론 안 보이던 '구도'를 드러냄. 잔차 추정 기반이라 '의심' 톤. 의미 임계는 거래대금 비례(최소 200억).
export type Stance = { key: string; emoji: string; label: string; warn: boolean; hint: string };
export function stanceOf(it: DaytradeItem): Stance | null {
  const f = it.foreign_est, g = it.inst_est, r = it.retail_est;
  if (f == null || g == null) return null;
  const big = f + g;                                   // 큰손 합산(외+기)
  const retail = r ?? -(f + g);
  const bar = Math.max(200, ((it.amount ?? 0) / 1e8) * 0.01);   // 의미 임계(거래대금 1%·최소 200억)
  const up = (it.change_pct ?? 0) > 0.5, dn = (it.change_pct ?? 0) < -0.5;
  if (up && big <= -bar && retail >= bar)
    return { key: 'unload', emoji: '🚨', label: '물량받이', warn: true, hint: `외국인·기관이 ${fmtEok(big)} 빠져나가는 걸 개인이 받아 올리는 위태로운 상승이에요 — 개인이 지치면 밀릴 수 있어요` };
  if (dn && retail <= -bar && big >= bar)
    return { key: 'surrender', emoji: '🤝', label: '개인 투항', warn: false, hint: `개인이 던지는 물량을 외국인·기관이 받아내는 중이라 바닥 근처 신호일 수 있어요` };
  if (f >= bar && g >= bar)
    return { key: 'double', emoji: '💪', label: '쌍끌이', warn: false, hint: `외국인·기관이 함께 사들이는 견조한 흐름이에요` };
  if (dn && big <= -bar)
    return { key: 'exit', emoji: '🏃', label: '큰손 이탈', warn: true, hint: `하락에 외국인·기관이 함께 파는 중이라 반등 기대가 약해요` };
  if ((f > 0) !== (g > 0) && Math.abs(f) >= bar && Math.abs(g) >= bar)
    return { key: 'swap', emoji: '↔', label: '손바뀜', warn: false, hint: `외국인과 기관의 방향이 엇갈리고 있어요` };
  return null;   // 뚜렷한 구도 없음
}
const STANCE_LABEL: Record<string, string> = { unload: '물량받이', surrender: '개인 투항', double: '쌍끌이', exit: '큰손 이탈', swap: '손바뀜' };

// ── 공유 스토어 (localStorage로 하루 유지 — 새로고침에도 안 날아감, 다음날 자동 초기화) ──
const _STORE = 'dt_alerts';
function _today(): string { const d = new Date(); return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`; }   // 로컬(KST) 날짜

// 🚪 단타탭 전면 개편 v3 — 화면·피드엔 '화면 알림'만: 큰손(flow_buy/flow_sell)·큰손 매수 시작(flowearly)·
//   핀 도구(손절 stop/목표 target/이탈 breakdown/이익보호 trail/시간손절 timestop/밤새 보유 판단 overnight)·내 가격알림(pricealert),
//   그리고 👁 감시 종목 알림(종류 무관 — 사용자가 지목한 종목). 나머지(급변·반등·초입·주도주·테마·순환매·국면·바닥류·호가 등)는
//   감지·알림 기록(logAlert)은 그대로 두고 노출만 차단한다(완전 가역 — 이 세트만 넓히면 승격). DAYTRADE_ZERO_PLAN 절대 규칙 1조.
//   ⚠️ 게이트는 울림(addAlerts)와 복원(_restore) 양쪽에 — 신규 알림뿐 아니라 배포 전 쌓인 옛 화면 밖도 화면에서 사라져야 함.
//   🔇 **70 독트린 화면 재편(07-28)** — 화면엔 속보류 + 시험 중 종합 신호만. 🏦큰손(flow_buy/flow_sell)·
//     ⏩큰손 매수 시작(flowearly)·🦵하이닉스 힘 감지(knee)은 **검증 중으로 강등**(감지·알림 기록 채점·그림자 A/B는 전부 그대로 — 화면만 내려놓음).
//     근거(07-28 판독): flow_buy 47.9%·flowearly 56.4%·flow_sell 57.8%·knee 59.5% — 전부 70 미달.
//     ▶ 되돌리는 법: 아래 목록에 그 kind를 도로 넣고 **서버 market_events.SURVIVOR_KINDS에도 같이** 넣으면 끝
//       (한쪽만 넣으면 조용히 반쪽만 복귀한다 — 화면엔 뜨는데 서버가 안 서빙하거나 그 반대).
const _SURVIVOR_FEED_KINDS = new Set<DtAlertKind>([
  'stop', 'target', 'breakdown', 'trail', 'timestop', 'overnight', 'pricealert',   // 📌 핀(내 종목) 계열은 유지 — 내가 건 것
  'news',   // 🗞️ 신호 영향 뉴스(07-21) — 서버 is_survivor와 동일하게 화면·복원 통과(방향 무관 주의성 알림)
  'lifeline',   // 🔌 데이터 끊김·복구(07-25 신설) — 서버 SURVIVOR_KINDS와 동일. 화면·소리만(잠금화면 푸시는 서버에서 제외).
  // 🌅 아침 판정 5종(08-04 사용자 결재 "5개 투입해") — 서버 market_events._SWITCHABLE_KINDS와 **같은 목록**.
  //   기본 ON, 끄는 건 서버 스위치(storage `alert_kind_switch`)라 여기 목록은 고정으로 둔다.
  //   잠금화면 푸시는 서버가 계속 제외(push.PUSH_EXCLUDED_KINDS — 모바일 미사용 방침).
  'morning_nobuy', 'morning_dipbuy', 'morning_inverse', 'morning_revert', 'expiry_kosdaq',
  // 📄 공시 경고(08-12) — 서버 market_events._SWITCHABLE_KINDS와 **같은 목록**. 08-11 정식 채점 통과분이라
  //   처음부터 화면+소리(사용자 사전 승인). 끄는 건 서버 스위치(storage `alert_kind_switch`).
  'disclosure_warning',
  // 🐋 큰손 매수 뒤 하이닉스 반응(08-15 임시 합격) — 서버 market_events._SWITCHABLE_KINDS와 **같은 목록**.
  //   08-22 최종 합격(표본 35 · 5분 승률 85.7% · 윌슨 95% 아래끝 70.6 > 70). 끄는 건 서버
  //   스위치(storage `alert_kind_switch`)라 여기 목록은 고정. 폰 푸시는 서버가 제외(push.PUSH_EXCLUDED_KINDS).
  //   방향 게이트가 없는 이유: 이 신호는 **매수 반응만** 기록한다(서버 flow_react가 dir=+1 고정) — 매도가 샐 길이 없다.
  'flowreact',
  // 🌙 급락일 밤 반등(08-20 사용자 결재) — 서버 market_events._SWITCHABLE_KINDS와 **같은 목록**.
  //   26년 전수 290번 · 10번에 6.6번 · 5년 토막 5/5가 기준선 초과라 처음부터 화면+소리.
  //   끄는 건 서버 스위치(storage `alert_kind_switch`)라 여기 목록은 고정. 폰 푸시는 서버가 제외.
  'night_rebound',
  //   ⏩ 큰손 매수 시작 90초 확인(flowearly_confirmed)은 **방향 게이트**가 있어 이 세트에 넣지 않는다 —
  //     아래 isSurvivorFeed의 `fdir === 'buy'` 분기가 규칙(08-08 원상복구). 세트에 넣으면 매도까지 샌다.
  //   🎖️ prime(종합 신호 자리)은 08-01 검증 중으로 강등으로 이 세트에서 빠짐(n=93·승률 51.6%, 독트린 70 미달).
  //     발행·채점·기준값 재보정은 그대로 — 화면 노출만 차단(가역). 되돌리려면 서버 SURVIVOR_KINDS와 **같이** 넣을 것.
  //   🎯 stockmove(하이닉스 가격 경보)는 07-24 강등으로 이 세트에서 빠짐(사용자 판정 "가격만 보는 건 도움 안 됨") — 감지·알림 기록은 유지, 화면 노출만 차단(가역). knee와 화면 알림 교대.
]);
export function isSurvivorFeed(a: DtAlert): boolean {
  if (_SURVIVOR_FEED_KINDS.has(a.kind)) return true;
  // ⚡ 지수 급락 경보 복귀(07-21) — marketmove는 **하락(dir<0)만** 피드·푸시 노출, 급등은 검증 중 유지.
  //   서버 is_survivor와 동일 조건(같은 자). set에 안 넣는 이유: 급등(dir=1)까지 통과되면 안 됨.
  //   ⚡ 08-04 '조짐'(2분 fast 변형) 검증 중으로 강등 — 확정 급락(4분 0.7%p)만 남긴다. 하루 45건이 울려
  //     화면의 바닥 소음이 됐고, 그 소음이 확정 급락까지 같이 묻었다. 감지·알림 기록(marketmove_fast 계정)은 유지.
  //     판별은 서버가 실어 보내는 알림 기록 계정(jk) — 이름의 '조짐'은 jk 이전 배포분(localStorage 복원) 호환.
  //     ▶ 되돌리는 법: 이 한 줄의 isFast 조건과 서버 _is_fast_marketmove 호출을 **같이** 지우면 끝.
  if (a.kind === 'marketmove' && (a.dir ?? 0) < 0) {
    const isFast = a.jk === 'marketmove_fast' || (a.name || '').includes('조짐');
    return !isFast;
  }
  // ⏩ 08-04(밤) 큰손 매수 시작 교체 — 화면·소리는 'flowearly_confirmed'(90초 확인)가 승계했고,
  //   확인 전 원본 flowearly는 검증 중으로 내려갔다(알림 기록 채점은 계속 — 90초 확인과의 A/B 대조 원료).
  //   ⏩ **08-08 원상복구** — 90초 확인도 확인 전 원본과 같이 **매수만** 화면·소리다. 08-04 결재 원문이
  //     "큰손 매수 시작 **매수** → 90초 확인 교체"인데 교체 구현이 방향을 안 보고 통과시켜 매도까지 새어나갔다.
  //     매도는 검증 중(알림 기록 채점·A/B는 서버에서 그대로) — 90초 확인 매도 실전 33.3%(n6)·소급 43%.
  //     서버 market_events.is_survivor의 flowearly_confirmed 분기와 **같은 조건**(항상 한 쌍으로 고칠 것).
  //   ▶ 되돌리는 법: 아래 한 줄을 지우고 위 _SURVIVOR_FEED_KINDS에 'flowearly_confirmed'를 도로 넣은 뒤,
  //     서버 is_survivor의 같은 분기도 **함께** 되돌리면 끝(한쪽만 하면 반쪽 복귀).
  if (a.kind === 'flowearly_confirmed') return a.fdir === 'buy' || (a.dir ?? 0) > 0;
  // 👁 감시 종목 알림 — 사용자가 명시적으로 지켜보는 종목의 소식은 종류 무관 노출(도구·사용자 주도)
  try { return !!a.ticker && !isMarketAlert(a.kind) && new Set(getWatch()).has(a.ticker); } catch { return false; }
}

function _restore(): DtAlert[] {
  try {
    const raw = JSON.parse(localStorage.getItem(_STORE) || 'null');
    if (raw && raw.date === _today() && Array.isArray(raw.alerts)) {
      let list: DtAlert[] = raw.alerts;
      // 🧹 일회성 청소 — 알림 스펙 전면 개편(선행 원칙·🔥초입 도입) 전 쌓인 옛 알림 전체 비움. 이후 새 스펙 알림만 누적.
      //   이미 울린 dedup 상태(급변/순환매/큰손레그)는 건드리지 않음 → 새로고침 즉시 재울림 스팸 없음.
      if (localStorage.getItem('dt_purge_v3') !== '1') {
        try {
          localStorage.setItem('dt_purge_v3', '1');
          localStorage.setItem(_STORE, JSON.stringify({ date: _today(), alerts: [] }));
        } catch { /* noop */ }
        return [];
      }
      return list.filter(isSurvivorFeed);   // 오늘 것만 복원 + 🚪 v3 배포 전 쌓인 옛 비화면 알림 제거(다음 _persist가 정리분 저장)
    }
  } catch { /* noop */ }
  return [];
}
function _persist() {
  try { localStorage.setItem(_STORE, JSON.stringify({ date: _today(), alerts: _alerts })); } catch { /* noop */ }
}
// 🚪 07-22 ③ — _alerts 복원은 여기서 하지 않는다. _restore→isSurvivorFeed가 isMarketAlert(위 :372)·getWatch·_WATCH를
//   호출하는데, 옛 코드는 이 줄에서 즉시 _restore()를 불러(그 정의들보다 먼저) 모듈 초기화 순서 버그를 냈다.
//   빈 배열로 선언만 하고, 실제 복원은 모든 의존이 정의된 파일 하단(getWatch 정의 뒤)에서 1회 실행한다.
let _alerts: DtAlert[] = [];
const _subs = new Set<() => void>();
const _seen = new Set<string>();
function _emit() { _subs.forEach((f) => f()); }
export function getAlerts(): DtAlert[] { return _alerts; }
export function clearAlerts() { _alerts = []; _persist(); _emit(); }
// 🌀 현재 장전(이상하게 조용함)된 종목 — 알림 카드 배지용. 60분 지난 건 만료 제외.
export function getQuietArmed(): { ticker: string; name: string }[] {
  const now = Date.now();
  return Object.entries(_quietArmed).filter(([, v]) => now - v.t <= 60 * 60 * 1000).map(([ticker, v]) => ({ ticker, name: v.name }));
}
// 🏦 큰손·국면(시장) 알림 — 종목 알림과 카테고리를 나눠 따로 보관하기 위한 분류.
// 시장(전체) 알림 여부 — 피드에서 '🏦 큰손·국면' 구역으로 분류(종목 알림과 분리). 단일 소스(중복 금지).
// ⚠️ 07-22 ③ — 반드시 '함수 선언'(호이스팅)으로 유지할 것. 옛 `const 화살표`였을 때, 모듈 초기화 시점의
//   _restore()(→isSurvivorFeed)가 이 정의보다 먼저 실행돼 ES5 트랜스파일에서 undefined 호출 TypeError가 났고,
//   try/catch가 false를 반환해 감시 종목의 비화면 알림이 복원마다 전량 탈락했다. 함수 선언은 항상 호이스팅돼 안전.
export function isMarketAlert(k: DtAlertKind): boolean {
  // 🌅 아침 판정 5종·⏩ 90초 확인도 시장 단위(종목 열 아님·감시 필터에 걸려 사라지면 안 됨)
  if (k === 'flowearly_confirmed' || k === 'morning_nobuy' || k === 'morning_dipbuy'
    || k === 'morning_inverse' || k === 'morning_revert' || k === 'expiry_kosdaq') return true;
  // 🐋 큰손 매수 뒤 하이닉스 반응(08-15)도 시장 단위 — 하이닉스로 재긴 하지만 '큰손 수급 → 지수 방향' 신호다
  //   (🦵 knee·🎯 stockmove와 같은 취급). 안 넣으면 감시 종목만 보기 모드·소리 필터에서 조용히 사라진다.
  if (k === 'flowreact') return true;
  // 🌙 급락일 밤 반등(08-20)도 시장 단위 — 코스피 지수 하나로 판정하는 하루 단위 신호(종목 열 아님).
  if (k === 'night_rebound') return true;
  return k === 'flow_buy' || k === 'flow_sell' || k === 'flowearly' || k === 'regime' || k === 'rotation' || k === 'marketmove' || k === 'reversal' || k === 'confluence' || k === 'dayshift' || k === 'theme' || k === 'leaderbreak' || k === 'futflow' || k === 'absorb' || k === 'bungee' || k === 'obimb' || k === 'driveflip' || k === 'ridefade' || k === 'ridegain' || k === 'news' || k === 'stockmove' || k === 'knee' || k === 'prime' || k === 'lifeline';   // 🔌 데이터 끊김·복구도 종목이 아니라 서버 전체 이야기(종목 필터·감시만 모드에 걸려 사라지면 안 됨). 🎯 탑승 추적 = 시장 수급 흐름(코스피 외국인 등) — 종목 열 아님(07-07 피드백). 🗞️ 뉴스도 시장 단위(종목 아님). 🎯 stockmove·🦵 knee=하이닉스 시장방향 신호(감시 아님·watchonly 소리 켜짐 제외)
}

// 🎨 알림 문구 3계층 파싱 — 한 문장에 태그가 여러 개 붙어 안 읽히던 문제(전면 UI 개편 A).
//   ' · '로 쪼개 [0]=헤드라인(무슨 일·얼마나), 마커(⏱📊⚠️⚡🔗⏳🕯️…)로 시작하는 조각=칩(판단 재료),
//   나머지=흐린 상세. 헤드라인 끝의 수급 스냅샷 괄호(외/기/개미)도 칩으로 분리. emit 쪽은 무변경(알림 기록·푸시는 평문 유지) — 렌더만 구조화.
export type AlertChipTone = 'warn' | 'model' | 'info' | 'flow' | 'neutral';
export type ParsedAlert = { head: string; chips: { text: string; tone: AlertChipTone }[]; detail: string };
const _CHIP_LEAD = /^(⚠️|⏱|📊|⚡|🔗|⏳|🕯️|🔥|↩︎|⏸)/;
const _SUPPLY_PAREN = /\s*\((외국인|외)[^)]*?(개미|기관|기)[^)]*\)\s*$/;
export function parseAlertMsg(msg: string): ParsedAlert {
  const parts = (msg || '').split(' · ').map((s) => s.trim()).filter(Boolean);
  let head = parts[0] ?? (msg || '');
  const chips: { text: string; tone: AlertChipTone }[] = [];
  const m = head.match(_SUPPLY_PAREN);   // 헤드라인 끝 수급 스냅샷 → 회색 칩
  if (m && m.index != null) { head = head.slice(0, m.index).trim(); chips.push({ text: m[0].trim().replace(/^\(|\)$/g, ''), tone: 'flow' }); }
  const detail: string[] = [];
  for (const seg of parts.slice(1)) {
    if (_CHIP_LEAD.test(seg)) {
      const tone: AlertChipTone = seg.startsWith('📊') ? 'model'
        : (seg.startsWith('⚠️') || seg.startsWith('⏱') || seg.startsWith('⏳') || seg.startsWith('🔗')) ? 'warn'
        : (seg.startsWith('⚡') || seg.startsWith('🕯️')) ? 'info' : 'neutral';
      chips.push({ text: seg, tone });
    } else {
      detail.push(seg);
    }
  }
  return { head, chips, detail: detail.join(' · ') };
}

// 내림 경고음 판정 — 문구가 아니라 구조 필드(방향)로 판정(07-21 문구 재작성으로 텍스트 스니핑 제거, 동작은 동일).
//   매도류(fdir='sell'·flow_sell·ridefade) + 방향 −1 급락/재가속류 = 내림 2음, 그 외 = 오름 2음.
const _isUrgentAlert = (a: DtAlert): boolean => a.kind === 'flow_sell'
  || a.kind === 'ridefade'                                   // 🎯 탄 수급 소진 = 출구 경고(내림음). ridegain(증축)은 오름음.
  || a.fdir === 'sell'                                       // ⏩ 큰손 매수 시작 매도·큰손 매도
  || ((a.kind === 'ignition' || a.kind === 'surge') && (a.dir ?? 0) < 0)   // 급락 초입·하락 재가속/상승 반납
  || (a.kind === 'regime' && a.verdict === '역풍')          // (피드 미도달이나 보존)
  || a.kind === 'morning_nobuy' || a.kind === 'morning_inverse'   // 🌅 '사지 마세요'·'인버스 우위' = 주의 쪽 판정 → 내림음
  || a.kind === 'disclosure_warning'                              // 📄 공시 경고 = 내 종목이 눌릴 수 있다는 경고(항상 dir −1) → 내림음
  || ((a.kind === 'marketmove' || a.kind === 'reversal' || a.kind === 'stockmove' || a.kind === 'knee' || a.kind === 'prime') && (a.dir ?? 0) < 0);   // 🎯 하이닉스 급락·🦵 매도 힘 감지도 내림음(매수 힘/급등은 오름음)

// 🚪 피드 판정(07-22 ④) — 화면 알림은 옛 dt_off_(isKindEnabled)에서 면제. 피드엔 화면 알림만 오므로(아래 filter가 이걸로 판정)
//   판정은 isSurvivorFeed 하나로 충분하다. 근본 원인: 화면 알림에도 dt_off_<kind> 플래그가 걸렸는데 되돌릴 UI가 v3에서
//   언마운트돼 복구 불가였다(화면 알림이 조용히 소거). ⚙ 소리 토글(dt_sound_)은 별개 판정(공인된 수동 침묵) — 여기 무관.
export function admitToFeed(a: DtAlert): boolean { return isSurvivorFeed(a); }

// ③ 합류(confluence)는 서버(market_events.py)로 이관 — 기기간 동일 이벤트 보장(클라 계산 시 기기마다 시각/id 달라짐).
// (07-22 ② 테스트용으로 export — 폭주 시 화면 알림 개별 통보 회귀 잠금. 동작 변화 없음.)
export function addAlerts(fresh: DtAlert[], opts?: { silent?: boolean }) {
  fresh = fresh.filter(admitToFeed);   // 🚪 v3 화면 알림만 + dt_off_ 면제(④). 감지·알림 기록은 상단 detectStock/logAlert에서 이미 수행
  if (!fresh.length) return;
  for (const a of fresh) if (a.ts == null) a.ts = Date.now();   // 울림 시각 스탬프(🅑 결과·🅓 브리핑용)
  // 카테고리별 별도 보관 — 종목 알림이 아무리 쏟아져도 드문 큰손/국면이 떠밀려 사라지지 않게(그날치 유지·다음 거래일 자동 초기화).
  const merged = [...fresh, ..._alerts];
  const keep = new Set([
    ...merged.filter((a) => isMarketAlert(a.kind)).slice(0, 600),   // 🏦 큰손·국면: 하루 600개(급변장은 분당 2~3개라 120으론 오래된 게 밀려 사라졌음)
    ...merged.filter((a) => !isMarketAlert(a.kind)).slice(0, 500),  // 📋 종목 알림: 500개
  ].map((a) => a.id));
  // id 디둡(서버 이벤트는 고정 id라 중복 유입 가능) + 울림 시각(ts) 내림차순 — 백필 이벤트가 진짜 시각 자리에 꽂히게.
  const _seenId = new Set<string>();
  _alerts = merged.filter((a) => { if (!keep.has(a.id) || _seenId.has(a.id)) return false; _seenId.add(a.id); return true; })
    .sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
  _persist();
  _emit();
  if (opts?.silent) return;   // 🌐 서버 백필(오래된 이벤트)은 피드에만 — 소리·푸시 없이(복귀 시 몰아치는 알림 방지)
  // 💤 07-22 ② 폭주 다이제스트 재움(v3) — 여기 오는 fresh는 이미 '화면 알림'뿐(:admitToFeed 판정). 화면 알림은 전부 울려야 하는데
  //   (패닉일=핀 손절 소리가 가장 중요한 날) 옛 32종 감지기 홍수용 5분 묶음 억제가 그 소리를 삼켰다. 그래서 폭주 억제를
  //   통째로 걷어낸다 — fresh(화면 알림 전건)를 바로 개별 통보·소리를 켠다. 기계(_burstTimes·_digestPending·_flushDigest)는 아래에
  //   주석으로 보존(되살리려면 그 주석 + 이 블록 복원). 근거: v3 "화면 알림은 전부 울린다" + 사용자 "푸시 총량 제한 금지".
  notify(fresh);
  // ① 2층제 사운드 — '행동층'(isSoundKind)만 소리 켜짐. 나머지 맥락층은 피드만·소리 꺼짐. entry는 ★확신(검증)일 때만.
  //   (감지기 32종은 다 피드에 뜨고 알림 기록에 채점됨 — '입 다물고 실력으로 증명' 구조. ⚙에서 🔔로 개별 승격.)
  if (isSoundEnabled()) {
    // 🔊 소리는 성적이 아니라 '화면에 나가는지'로(07-22 전면 수리) — fresh는 이미 화면 알림뿐이니 shouldSoundAlert로만 거른다.
    //   (성적 소리 켜짐 판정 isSoundKind로 또 거르면 marketmove·flow_sell이 누적 저성적으로 소리가 꺼지던 게 이번 사고의 뿌리.)
    let sounding = fresh.filter(shouldSoundAlert);
    // 👁 '감시만' 모드 = 종목 알림만 감시 종목으로 거름. 시장 알림(🏦큰손·⚡국면…)은 항상 소리(드물고 중요).
    try {
      if (localStorage.getItem('dt_alert_watchonly') === '1') {
        const _w = new Set(getWatch());
        // 📄 공시 경고(08-12)는 '감시만' 모드에서도 항상 소리 — 애초에 **내가 가진 종목(핀·감시·보유)에만**
        //   울리는 알림이라 이미 '감시만'의 취지를 통과한 상태다. 여기서 걸러 버리면 보유·핀 종목의 경고가
        //   화면엔 뜨는데 소리는 안 나는(= 놓치는) 반쪽 알림이 된다.
        sounding = sounding.filter((a) => isMarketAlert(a.kind) || a.kind === 'disclosure_warning' || !a.ticker || _w.has(a.ticker));
      }
    } catch { /* noop */ }
    if (sounding.length) {
      const urgent = sounding.some(_isUrgentAlert);
      // 🗞️ 뉴스·🔌 데이터 끊김/복구처럼 '시장 방향이 없는' 알림만이면 중립음(오름=호재음으로 오인되지 않게).
      //   데이터 끊김에 내림음을 쓰면 "지수가 빠졌다"로 오해할 수 있어 일부러 중립음(시장 사건이 아니라 서버 사건).
      const allNeutral = sounding.every((a) => a.kind === 'news' || a.kind === 'lifeline');
      playChime(urgent ? 'down' : allNeutral ? 'neutral' : 'up');
    }
  }
}

/* 💤 07-22 ② 재움(v3) — 폭주 다이제스트 flush(쌓인 폭주 알림을 종류별 카운트 한 줄 요약으로 1건 울림). 화면 알림은
   전부 개별 통보하므로 미사용. 코드 보존 — 되살리려면 위 상태 변수 + addAlerts 다이제스트 블록과 함께 주석 해제.
function _flushDigest() {
  const items = _digestPending; _digestPending = []; _digestLastFlush = Date.now();
  if (!items.length) return;
  const counts: Record<string, number> = {};
  for (const a of items) counts[a.kind] = (counts[a.kind] ?? 0) + 1;
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const parts = top.slice(0, 4).map(([k, n]) => `${ALERT_KIND[k as DtAlertKind].label} ${n}건`);   // 이모지 없이 종류·건수만
  const body = parts.join(' · ') + (top.length > 4 ? ' 외' : '');
  if (isPushEnabled()) { try { new Notification(`알림 ${items.length}건이 한꺼번에 왔어요`, { body, tag: 'digest' }); } catch { } }
  if (isSoundEnabled()) playChime(items.some(_isUrgentAlert));   // 급락 폭주 요약인데 오름음 나던 버그 수정 — 내용이 톤을 정함
}
*/

// 🅑 결과 꼬리표 — 울린 종목 알림에 '그 후 ±%·경과분'을 현재가로 채워 넣음(60분에 고정).
// dir(+1/−1)로 UI가 ✅/❌ 판정. 스캔틱마다 호출(현재 items 가격 사용). 성적표(서버 채점)와 별개인 '개별 알림 즉시 피드백'.
function annotateOutcomes(items: DaytradeItem[]) {
  const now = Date.now();
  const px: Record<string, number> = {};
  for (const it of items) if (it.live && it.price) px[it.ticker] = it.price;
  let changed = false;
  for (const a of _alerts) {
    if (!a.ticker || a.px == null || a.ts == null) continue;
    if (a.outcome && a.outcome.min >= 60) continue;   // 60분 지나면 고정
    const cur = px[a.ticker];
    if (!cur) continue;
    const min = Math.floor((now - a.ts) / 60000);
    if (min < 5) continue;                             // 5분 미만은 노이즈라 표기 안 함
    a.outcome = { pct: +((cur / a.px - 1) * 100).toFixed(1), min: Math.min(min, 60) };
    changed = true;
  }
  if (changed) { _persist(); _emit(); }
}

// ── 알림 사운드(Web Audio — 오디오 파일 없이 차임 생성) ─────────
let _audioCtx: AudioContext | null = null;
function _getCtx(): AudioContext | null {
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    if (!_audioCtx) _audioCtx = new AC();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    return _audioCtx;
  } catch { return null; }
}
// 자동재생 정책 — 브라우저는 사용자 제스처 전엔 소리를 막는다. 리스너를 '계속 살려두어'(self-remove X)
// 새로고침·탭 전환으로 오디오가 다시 잠겨도 다음 클릭/키에서 자동 재해제. 포그라운드 복귀 때도 resume 시도.
if (typeof document !== 'undefined') {
  const ensureAudio = () => { const c = _getCtx(); if (c && c.state === 'suspended') c.resume().catch(() => {}); };
  document.addEventListener('click', ensureAudio);
  document.addEventListener('keydown', ensureAudio);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) ensureAudio(); });
}
function _beep(ctx: AudioContext, freq: number, at: number, dur: number, vol = 0.18) {
  const o = ctx.createOscillator(), g = ctx.createGain();
  o.type = 'sine'; o.frequency.value = freq;
  const t = ctx.currentTime + at;
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(vol, t + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(ctx.destination);
  o.start(t); o.stop(t + dur + 0.03);
}
// tone: true/'down'=내림(경고) · false/'up'=오름(호재) · 'neutral'=중립(방향 없는 주의, 🗞️뉴스). boolean 유지로 기존 호출 호환.
export function playChime(tone: boolean | 'up' | 'down' | 'neutral') {
  const ctx = _getCtx();
  if (!ctx) return;
  const t = tone === true ? 'down' : tone === false ? 'up' : tone;
  if (t === 'down') { _beep(ctx, 660, 0, 0.13); _beep(ctx, 415, 0.15, 0.22); }        // 내림 2음 — 경고(손절·매도·급락)
  else if (t === 'neutral') { _beep(ctx, 880, 0, 0.12); _beep(ctx, 880, 0.16, 0.16); } // 중립 2음(같은 음 반복) — 방향 없는 주의(🗞️뉴스)
  else { _beep(ctx, 784, 0, 0.11); _beep(ctx, 1175, 0.13, 0.18); }                     // 오름 2음 — 호재(매수·목표·살자리)
}

// ── 사운드 on/off (기본 ON, push와 독립) ────────────────────────
export function isSoundEnabled(): boolean {
  try { return localStorage.getItem('dt_sound') !== '0'; } catch { return true; }
}
export function setSoundEnabled(on: boolean) {
  try { localStorage.setItem('dt_sound', on ? '1' : '0'); } catch { /* noop */ }
  if (on) playChime(false);   // 켤 때 한 번 들려줘 확인
}

// ── 🫀 시장 펄스 스토어 — 시세·수급 시계열을 쌓아 '오르내림'을 실시간으로(알림 사각지대 보완) ──
// 엔진(armed·모든 탭)과 단타 탭(열려 있을 때)이 둘 다 addPulse를 부르고, 45초 throttle로 한 점만 쌓음.
export interface PulsePoint { t: number; kospi: number | null; kosdaq: number | null; frgn?: number; inst?: number; pen?: number; indi?: number; fut?: number; pgm?: number; bas?: number; ctr?: number; hfr?: number; hin?: number; hid?: number; hst?: number; hob?: number; }   // pgm/bas/ctr: ⚡실시간 층(07-09) · h*: 🎯 하이닉스 패널(#107)
const _PULSE = 'dt_pulse2';   // v2(07-08) — 가짜 0 오염분 폐기
function _restorePulse(): PulsePoint[] {
  try { const r = JSON.parse(localStorage.getItem(_PULSE) || 'null'); if (r && r.date === _today() && Array.isArray(r.pts)) return r.pts; } catch { /* noop */ }
  return [];
}
let _pulse: PulsePoint[] = _restorePulse();
const _pulseSubs = new Set<() => void>();
export function addPulse(d: DaytradeBackdrop) {
  const now = Date.now();
  if (_pulse.length && now - _pulse[_pulse.length - 1].t < 25000) return;   // ~25초 throttle(07-09 1분 신선도 창 지원 — 45초→25초)
  const k = (d.markets?.KOSPI || {}) as { foreign?: number | null; institution?: number | null; pension?: number | null; individual?: number | null };
  const _pt: PulsePoint = { t: now, kospi: d.kospi_pct ?? null, kosdaq: d.kosdaq_pct ?? null };
  if (k.foreign != null) _pt.frgn = k.foreign;
  if (k.institution != null) _pt.inst = k.institution;
  // '모름(null)'을 0으로 위조하지 않음 — 가짜 0가 10분 기준점이 되면 '세게 던지는 중' 오판(07-08 선물 카드 사고)
  if (k.pension != null) _pt.pen = k.pension;
  if (k.individual != null) _pt.indi = k.individual;
  const _fv = d.futures?.foreign as number | undefined;
  if (_fv != null) _pt.fut = _fv;
  const _rt = d.rt || {};   // ⚡ 실시간 층(프로그램 억·베이시스·선물 체결강도) — null 생략 규칙 동일
  if (_rt.pgm_eok != null) _pt.pgm = _rt.pgm_eok;
  if (_rt.basis != null) _pt.bas = _rt.basis;
  if (_rt.fut_cttr != null) _pt.ctr = _rt.fut_cttr;
  const _hx = d.hyx || {};   // 🎯 하이닉스 패널(#107) — null 생략 규칙 동일(가짜 0 금지)
  if (_hx.flow?.frgn != null) _pt.hfr = _hx.flow.frgn;
  if (_hx.flow?.inst != null) _pt.hin = _hx.flow.inst;
  if (_hx.flow?.indi != null) _pt.hid = _hx.flow.indi;
  if (_hx.strength != null) _pt.hst = _hx.strength;
  if (_hx.ob != null) _pt.hob = _hx.ob;
  _pulse = [..._pulse, _pt].slice(-60);
  try { localStorage.setItem(_PULSE, JSON.stringify({ date: _today(), pts: _pulse })); } catch { /* noop */ }
  _pulseSubs.forEach((f) => f());
}
export function usePulse(): PulsePoint[] {
  const [, bump] = useState(0);
  useEffect(() => { const u = () => bump((n) => n + 1); _pulseSubs.add(u); return () => { _pulseSubs.delete(u); }; }, []);
  return _pulse;
}

// 📡 실시간 스트림(SSE) — 서버가 새 이벤트를 즉시 밀어줌: 전달 지연 30~60초 → ~2초(하이닉스 힘 감지 배관, 질적개선 A).
//   EventSource는 자동 재접속. 실패·미지원 환경에선 기존 30초 폴이 그대로 폴백(이중화).
let _es: EventSource | null = null;
export function startEventStream() {
  if (_es || typeof EventSource === 'undefined') return;
  try {
    _syncWatchToServer();   // 👁 앱 열릴 때 감시 목록 서버 반영
    _es = new EventSource('/api/daytrade/stream');
    _es.onmessage = (ev) => {
      try { ingestServerEvents([JSON.parse(ev.data) as MarketEvent]); } catch { /* noop */ }
    };
    _es.onerror = () => { /* 자동 재접속에 맡김 — 폴이 백업 */ };
  } catch { _es = null; }
}

// React 구독 — 피드 컴포넌트가 사용
export function useAlerts(): DtAlert[] {
  const [, bump] = useState(0);
  useEffect(() => {
    const u = () => bump((n) => n + 1);
    _subs.add(u);
    return () => { _subs.delete(u); };
  }, []);
  return _alerts;
}

// ── 브라우저 푸시(localStorage로 상태 공유) ─────────────────────
export function isPushEnabled(): boolean {
  try {
    return localStorage.getItem('dt_push') === '1'
      && typeof Notification !== 'undefined' && Notification.permission === 'granted';
  } catch { return false; }
}
export async function enablePush(): Promise<boolean> {
  if (typeof Notification === 'undefined') return false;
  let p = Notification.permission;
  if (p === 'default') p = await Notification.requestPermission();
  if (p !== 'granted') return false;
  try { localStorage.setItem('dt_push', '1'); } catch { /* noop */ }
  subscribeWebPush();   // 📲 잠금화면 푸시용 구독(서버가 큰손·국면·고점반전 송출). 실패해도 인앱/데스크톱 알림은 동작.
  return true;
}
// 🔕 앱 '알림' 토글 OFF — 인앱 플래그만 끄던 옛 버그(서버 웹푸시는 계속 옴)를 수리: 이 기기 구독을 실제로 해제하고
//   서버 endpoint까지 제거해 잠금화면 푸시를 원천 차단한다(다른 기기 무영향 — endpoint 단위).
export async function disablePush(): Promise<void> {
  try { localStorage.removeItem('dt_push'); } catch { /* noop */ }
  await unsubscribeWebPush();
}
export async function unsubscribeWebPush(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;   // 구독 없으면 할 일 없음(마이그레이션 재호출에도 안전)
    const endpoint = sub.endpoint;
    try { await postPushUnsubscribe(endpoint); } catch { /* 서버 제거 실패해도 로컬은 해제 — 다음 발송 시 410으로 서버가 자동 정리 */ }
    try { await sub.unsubscribe(); } catch { /* noop */ }
  } catch { /* noop */ }
}

// 웹푸시 구독 — SW 준비 → VAPID 공개키로 PushManager 구독 → 서버에 등록. 모바일 잠금화면 알림의 핵심.
function _urlB64ToUint8Array(base64: string): Uint8Array {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}
export async function subscribeWebPush(): Promise<void> {
  // 🔒 토글이 단일 판정 — 앱 로드·핀/감시 동기화·SW 회전 등 **어느 초기화 경로가 불러도** 알림 OFF면 구독을 안 만든다
  //   (해지 직후 재구독 유출 차단·마이그레이션과의 경합 무력화). 켜는 유일 경로 enablePush는 dt_push=1을 먼저 세팅하고
  //   부르므로 이 판정을 통과한다. ⚠️ 이 한 줄 게이트가 '구독 생성' 전체를 커버(호출 지점마다 게이트 대신 원천에서).
  if (!isPushEnabled()) return;
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const reg = await navigator.serviceWorker.ready;
    const { key } = await fetchPushKey();
    if (!key) return;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: _urlB64ToUint8Array(key) });
    await postPushSubscribe(sub.toJSON());
    syncPins();       // 구독 직후 현재 핀(보유) 목록도 서버에 등록
    syncWatch();      // 👁 감시 목록도 서버에 등록(감시 종목 잠금화면 푸시용)
    syncPushPref();   // 이 기기의 푸시 종류 선택도 서버에 등록
  } catch { /* noop */ }
}

// ── 잠금화면 푸시 종류 선택(기기별) — 서버가 종류별로 이 기기에 보낼지 필터 ──
// 서버가 실제로 푸시하는 4종만 대상. 미설정은 기본 ON(=기존처럼 다 받음).
export type PushCat = 'flow' | 'regime' | 'topreversal' | 'pin' | 'watch' | 'ticket' | 'news';   // ticket=🎫 뉴단타(관리자 전용) · news=🗞️ 신호 영향 뉴스(기본 켬)
// 🚪 v3 — 화면에 노출하는 푸시 종류는 '화면 알림'만(큰손·큰손 매수 시작·핀·감시 + 🗞️뉴스). 국면·고점반전은 소리 끄기(서버가 안 보냄).
//   PushCat 타입·서버 set_pref는 그대로 둔다(가역 — 승격 시 여기 항목만 되살리면 됨).
export const PUSH_CAT_META: { cat: PushCat; icon: string; label: string }[] = [
  { cat: 'flow', icon: '🏦', label: '큰손·매수 시작' },
  { cat: 'news', icon: '🗞️', label: '뉴스 속보' },
  { cat: 'pin', icon: '📌', label: '핀 손절/목표' },
  { cat: 'watch', icon: '👁', label: '감시 종목' },
];
const _PUSH_PREF = 'dt_push_pref';
export function getPushPref(): Record<PushCat, boolean> {
  const def: Record<PushCat, boolean> = { flow: true, regime: true, topreversal: true, pin: true, watch: true, ticket: false, news: true };   // ticket 기본 꺼짐(opt-in) · news 기본 켬(늦으면 안 되는 정보)
  try {
    const raw = JSON.parse(localStorage.getItem(_PUSH_PREF) || '{}');
    return { ...def, ...raw };
  } catch { return def; }
}
export function setPushPref(cat: PushCat, on: boolean): void {
  const pref = getPushPref();
  pref[cat] = on;
  try { localStorage.setItem(_PUSH_PREF, JSON.stringify(pref)); } catch { /* noop */ }
  syncPushPref();   // 바뀌면 즉시 서버 반영
}
export async function syncPushPref(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await postPushPref({ endpoint: sub.endpoint, pref: getPushPref() });
  } catch { /* noop */ }
}

// 📌 핀(보유) 손절/목표를 서버에 동기화 — 서버가 그 종목 도달 시 이 기기로 푸시(3단계). 구독 없으면 no-op.
export async function syncPins(): Promise<void> {
  // 📌 08-12 — 손절/목표 값은 아래처럼 푸시 구독이 있어야 보내지만, **핀 티커 목록만은 항상** 서버에 알린다.
  //   📄 공시 경고가 '내 핀 종목'을 대상으로 삼는데, 폰 푸시를 안 쓰면 서버가 핀을 하나도 몰랐다(반쪽 기능).
  _syncWatchToServer();
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const pinned = readJSON<string[]>('dt_pinned', []);
    const plans = readJSON<Record<string, PinPlan>>('dt_pin_plans', {});
    const names = readJSON<Record<string, string>>('dt_pin_names', {});
    const pins = pinned
      .filter((tk) => plans[tk]?.owned !== false)   // ❹ 관찰(미보유) 핀은 잠금화면 손절/목표 푸시 제외
      .map((tk) => ({ ticker: tk, name: names[tk] || tk, ...(plans[tk] || {}) }))
      .filter((p) => p.stop || p.target);
    await postPushPins({ endpoint: sub.endpoint, pins });
  } catch { /* noop */ }
}
// 국면·고점반전·큰손은 서버가 웹푸시(SW)로 보냄 → 클라 OS알림은 건너뛰어 중복 방지(인앱 피드엔 그대로 표시).
// ⏩ flowearly는 08-04부터 **여기서 뺀다** — 서버가 발행은 하되 웹푸시는 제외(push.PUSH_EXCLUDED_KINDS)하므로,
//   이 목록에 남겨두면 "서버가 푸시함"으로 오인돼 클라 OS알림까지 건너뛴다(양쪽 다 죽는 함정 — new-alert-kind 스킬 ②-5).
//   판단 기준은 '서버가 발행하나'가 아니라 '서버가 **웹푸시**를 보내나'. lifeline·prime이 같은 이유로 빠져 있다.
const _SERVER_PUSHED = new Set<DtAlertKind>(['regime', 'topreversal', 'flow_buy', 'flow_sell', 'theme', 'marketmove', 'reversal', 'obimb', 'driveflip', 'news', 'stockmove', 'knee']);   // 지수펄스·뉴스·하이닉스 힘 감지 루프(서버)가 웹푸시 — 인앱 OS알림 중복 방지. 🦵 knee=하이닉스 힘 감지 루프가 잠금화면 푸시(07-24 승격). stockmove는 강등돼 무푸시(잔류 무해)
// 🔉 중요도(severity 0~100) — 채널별 소비 임계의 축(알림개선 ④ v1: OS알림 게이트부터).
//   종류 기본점 + ★확신 +15 + 소리 켜짐 판정 +10. 소리 자체는 소리 켜짐 판정 제도 유지(이중 게이트 안 함).
export function sevOf(a: DtAlert): number {
  const base: Partial<Record<DtAlertKind, number>> = {
    confluence: 90, stop: 88, target: 82, lifeline: 80, regime: 80, flow_buy: 75, flow_sell: 75,   // 🔌 lifeline — 서버 웹푸시가 없으니(07-25 제외) PC OS알림 게이트(60)는 넘겨 눈에 띄게
    marketmove: 70, stockmove: 70, knee: 70, prime: 70, reversal: 70, dayshift: 65, rotation: 65, entry: 62,
    // ⏩ flowearly 45 — 08-04 임시 합격의 승인 범위는 **화면 피드 + 소리**까지다(윈도우 팝업은 승인 밖).
    //   ⚠️ 50이면 안 된다: 아래 소리 켜짐 판정 보너스 +10이 붙어 정확히 60이 되고, notify()의 OS알림 게이트가
    //   `< 60` 컷이라 60은 **통과**한다. 게다가 큰손 매수 시작의 화면 승격 근거(n36·66.7%·늦은 정도<최대 이익 폭)가 바로 그 소리 켜짐 판정
    //   '소리 켜짐' 조건이라 — **성적이 좋아지면 팝업이 뜨는** 역설이 된다. 45면 45+10=55로 어떤 경로에서도
    //   60 미만(큰손 매수 시작은 star를 안 달아 +15도 해당 없음). 게이트(:notify)는 건드리지 않는다 — 타 kind 파급.
    futflow: 55, leaderbreak: 55, obimb: 50, flowearly: 45, topreversal: 55,
    // ⏩ 90초 확인·🌅 아침 판정도 **45**로 같은 이유(위 주석) — 승인 범위는 화면 피드 + PC 소리까지고
    //   윈도우 OS 팝업(게이트 60)은 승인 밖이다. 45+소리 켜짐 보너스 10 = 55로 어떤 경로에서도 60 미만.
    flowearly_confirmed: 45, morning_nobuy: 45, morning_dipbuy: 45,
    morning_inverse: 45, morning_revert: 45, expiry_kosdaq: 45,
    // 📄 공시 경고도 **45** — 승인 범위가 '화면 + 소리'까지다(윈도우 OS 팝업 게이트 60은 승인 밖).
    //   45 + 소리 켜짐 보너스 10 = 55로 어떤 경로에서도 60 미만이다.
    disclosure_warning: 45,
    // 🐋 큰손 매수 뒤 하이닉스 반응도 **45** — 08-15 임시 합격 때 정한 승인 범위가 '화면 + PC 소리'까지다
    //   (윈도우 OS 팝업 게이트 60은 승인 밖). 45 + 소리 켜짐 보너스 10 = 55로 어떤 경로에서도 60 미만이다.
    //   ⚠️ 08-22 최종 합격은 **표시(잠정 뗌)만** 바꾼 결재라 이 값은 그대로 45다(승인 범위 확대 아님).
    flowreact: 45,
    // 🌙 급락일 밤 반등(08-20)도 **45** — 승인 범위가 '화면 + PC 소리'까지다(윈도우 OS 팝업 게이트 60은 승인 밖).
    //   45 + 소리 켜짐 보너스 10 = 55. star를 안 달아 +15도 해당 없으니 어떤 경로에서도 60 미만이다.
    night_rebound: 45,
  };
  let v = base[a.kind] ?? 40;
  if (a.star) v += 15;
  try { if (kindLicense(a.kind) === '발성') v += 10; } catch { /* noop */ }
  return Math.min(100, v);
}

function notify(fresh: DtAlert[]) {
  if (!isPushEnabled()) return;
  for (const a of [...fresh].reverse()) {
    if (_seen.has(a.id)) continue;
    _seen.add(a.id);
    if (_SERVER_PUSHED.has(a.kind)) continue;   // 서버 웹푸시가 담당
    if (!shouldSoundAlert(a)) continue;   // 🔊 OS알림도 소리와 같은 판정(07-22) — 옛 성적 소리 켜짐 판정(isSoundKind) 게이트가 OS알림도 동반 침묵시켰음. 이제 화면 알림+수동토글로만.
    if (sevOf(a) < 60) continue;   // 🔉 중요도 게이트 — OS알림은 sev 60+만(피드·소리는 별도 체계)
    // 푸시 제목/본문도 피드와 같은 표준(헤드라인/본문). 07-21 문구 재작성.
    const { head, body } = splitAlert(a.msg);
    try { new Notification(`${a.name} — ${head}`, { body: body || ALERT_KIND[a.kind].label, tag: a.id }); } catch { /* noop */ }
  }
}

// ── localStorage 읽기 헬퍼(핀/계획/이름) ────────────────────────
function readJSON<T>(key: string, fallback: T): T {
  try { return JSON.parse(localStorage.getItem(key) || '') as T; } catch { return fallback; }
}

// ── 👁 단타 감시 목록(기기 저장) ─────────────────────────────────
// 보유 핀(📌)과 별개 — '안 사도 계속 지켜볼' 종목. 스캔에 항상 끼워(pin 파라미터) 그 종목의 모든 알림(🔥초입·🐋큰손·✨살자리·🔻고점반전…)을 받음.
// 핀=보유(손절/목표 계획), 감시=관찰(계획 없이 알림만). 최대 8(스캔 pin 상한·종목당 실시간 구독 비용).
const _WATCH = 'dt_watch';
const _WATCH_NAMES = 'dt_watch_names';
export const WATCH_MAX = 8;
const _watchSubs = new Set<() => void>();
export function getWatch(): string[] { return readJSON<string[]>(_WATCH, []); }
export function getWatchNames(): Record<string, string> { return readJSON<Record<string, string>>(_WATCH_NAMES, {}); }
export function isWatched(ticker: string): boolean { return getWatch().includes(ticker); }

// 🧹 옛 dt_off_ 1회 청소(07-22 ④) — 화면 알림 kind에 남은 '끄기' 흔적(dt_off_<kind>='1')을 지운다. 그 흔적은 v3에서
//   언마운트된 AlertFeed의 setKindEnabled로만 되돌릴 수 있어 복구 불가 → 화면 알림을 조용히 소거하던 함정.
//   ⚠️ ⚙ 소리 수동 토글(dt_sound_)은 절대 건드리지 않는다 — 공인된 수동 침묵 수단(사용자 말). dt_purge_v3와 같은 1회 가드.
function _purgeSurvivorOff() {
  try {
    if (localStorage.getItem('dt_off_purge_v3') === '1') return;
    localStorage.setItem('dt_off_purge_v3', '1');
    _SURVIVOR_FEED_KINDS.forEach((k) => localStorage.removeItem('dt_off_' + k));
    localStorage.removeItem('dt_off_marketmove');   // marketmove(하락)도 화면 알림 → 잔존 dt_off_ 청소
  } catch { /* noop */ }
}

// 🚪 07-22 ③④ 부팅 초기화 — 반드시 여기서(isMarketAlert·getWatch·_WATCH 등 모든 의존이 정의된 뒤). 파일 상단에서
//   부르면 모듈 초기화 순서 버그로 감시 종목 비화면 알림이 전량 탈락한다(위 :372·:359 주석 참조).
_purgeSurvivorOff();          // ④ 화면 알림 kind의 옛 dt_off_ 1회 청소(dt_sound_ 불가침)
_alerts = _restore();         // ③ 복원 — 감시 종목 알림이 초기화 순서로 탈락하지 않게
// 반환: true=처리됨 / false=상한 초과로 추가 실패(호출부가 안내). 제거는 항상 true.
// 👁 감시 목록 서버 동기화(07-08) — 서버 감지(빠른 레인·호가쏠림)가 감시 종목을 알게(KODEX 침묵 사고).
//   📌 08-12 — 핀 티커 목록도 같이 보낸다(📄 공시 경고가 핀 종목을 대상에 넣어야 해서).
//   왜 여기인가: 핀의 서버 동기화(syncPins)는 **폰 푸시 구독이 있을 때만** 돌아서, 푸시를 안 쓰는
//   사용자는 서버에 핀이 하나도 없었다. 이 경로는 푸시와 무관하게 항상 도착한다.
function _syncWatchToServer() {
  try {
    const pins = readJSON<string[]>('dt_pinned', []);
    // ⚠️ .catch는 **바깥 사슬**에 건다(08-12) — 안쪽에 `postWatchSync(...).catch()`로 걸면 그 함수가
    //   프라미스가 아닌 걸 돌려주는 순간(시험 대역 등) `.catch of undefined`로 터지고, 이건 then 콜백
    //   안이라 위 try/catch가 못 잡아 **처리되지 않은 거부**가 된다(핀 동기화까지 이 경로를 타게 된 뒤
    //   실제로 jest 워커가 죽었다). 바깥에 걸면 import 실패·거부·비프라미스 반환을 한 번에 삼킨다.
    import('./api')
      .then(({ postWatchSync }) => postWatchSync(getWatch(), getWatchNames(), pins))
      .catch(() => { /* noop — 동기화 실패가 알림을 죽이지 않는다 */ });
  } catch { /* noop */ }
}

export function toggleWatch(ticker: string, name?: string): boolean {
  if (!ticker) return false;
  let list = getWatch();
  const names = getWatchNames();
  if (list.includes(ticker)) { list = list.filter((t) => t !== ticker); delete names[ticker]; }
  else {
    if (list.length >= WATCH_MAX) return false;
    list = [...list, ticker]; if (name) names[ticker] = name;
  }
  try { localStorage.setItem(_WATCH, JSON.stringify(list)); localStorage.setItem(_WATCH_NAMES, JSON.stringify(names)); } catch { /* noop */ }
  _watchSubs.forEach((f) => f());
  syncWatch();   // 서버에도 반영(잠금화면 푸시용) — 구독 없으면 no-op
  _syncWatchToServer();
  return true;
}
export function useWatch(): string[] {
  const [, bump] = useState(0);
  useEffect(() => { const u = () => bump((n) => n + 1); _watchSubs.add(u); return () => { _watchSubs.delete(u); }; }, []);
  return getWatch();
}
// 감시 목록을 서버에 동기화 — 서버가 그 종목을 재감지해 이 기기로만 잠금화면 푸시(2단계). 구독 없으면 no-op.
export async function syncWatch(): Promise<void> {
  try {
    if (!('serviceWorker' in navigator)) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const names = getWatchNames();
    const watch = getWatch().map((tk) => ({ ticker: tk, name: names[tk] || tk }));
    await postPushWatch({ endpoint: sub.endpoint, watch });
  } catch { /* noop */ }
}

// ── 감지 로직 ──────────────────────────────────────────────────
function detectStock(items: DaytradeItem[], pinned: string[], plans: Record<string, PinPlan>, names: Record<string, string>, prev: Record<string, { grade: string; price: number; above?: boolean; strength?: number; peak?: number; leader?: boolean; pscore?: number; brank?: number; stance?: string }>) {
  const time = new Date().toLocaleTimeString('ko-KR');
  const now = Date.now();
  const fresh: DtAlert[] = [];
  const verdict = _lastVerdict;   // 확신(★) 판정용 시장 배경
  // ⓐ 개장 유예 게이트 — 09:00~09:30 KST엔 종목 수급(외인/기관 추정)이 당일 집계 전이라 '전일 잔재'가 남음
  //   (누적 −1.9조가 금요일 값·5분 만에 담기↔던지기 뒤집힘). 수급성 종목 알림에 '전일잔재 가능' 태그로 정직하게.
  const _kstM = (() => { const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' })); return d.getHours() * 60 + d.getMinutes(); })();
  const earlyGrace = (_kstM >= 540 && _kstM < 570) ? ' 다만 장 초반이라 어제 수급이 섞여 있을 수 있어요.' : '';   // 본문에 붙는 문장 조각
  // 🌙 09-23 저녁장(평일 16:00~20:00, 거래소 애프터마켓 — 주식만, ETF 없음). 체결이 실제로 사고팔 수 있는 가격이라
  //   (신한투자증권 참여 확인) 📌핀 손절·목표·목표 절반은 저녁에도 울린다. 그 밖의 감지('오래 제자리'·'평균가 아래로'·
  //   '이익 되돌림 주의'·급등 시작·고점 반전·매수 자리·종목 큰손 등)는 낮 장 흐름용이라 거래가 얇은 저녁엔 헛울림만 낸다
  //   → 저녁엔 전부 쉰다(아래 루프 첫머리 분기). 저녁 감지는 서버도 채점 기록에 받지 않는다(routers/scanner.daytrade_alert_log).
  const _kstDay = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' })).getDay();
  const _evening = _kstDay >= 1 && _kstDay <= 5 && _kstM >= 960 && _kstM < 1200;
  const mk = (it: DaytradeItem, kind: DtAlertKind, msg: string, star = false, dir = 0): DtAlert =>
    ({ id: `${it.ticker}-${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
       time, name: names[it.ticker] || it.name, ticker: it.ticker, kind, msg, star, px: it.price, ts: Date.now(), dir,
       ...(_driveNow != null ? { drv: _driveNow } : {}) });
  // 🅐 오늘 장 성격 게이트 — 장 성격이 '롱 불리'(패닉/약세/변동성폭발)면 매수형 알림에 '참고만' 꼬리표(침묵 대신 온건).
  const chGate = (buy: boolean) => (buy && _lastCharacter?.long_favorable === false ? ` 지금은 ${_lastCharacter.character} 장이라 참고만 하세요.` : '');
  // 🤫 시장 동반 정적 가드 — 유니버스 대다수가 납작하면(점심 등) 개별 '정적'은 신호 아님 → 장전 억제.
  const _live = items.filter((i) => i.live);
  const _lull = _live.length >= 8 && _live.filter((i) => Math.abs(i.change_pct) < 0.5).length / _live.length > 0.6;
  const _watchSet = new Set(getWatch());   // 🎢 감시/핀 종목은 재가속 임계 더 예민하게
  // 📌 핀(보유) 손절/목표/이탈 — '레벨 기준'(크로싱 아님) + 1회 플래그(회복하면 재무장). p 없어도 평가 →
  // 새로고침 순간 가격이 손절선을 지나도 누락 없이 잡힘(보유 포지션이라 가장 중요). 영속 플래그라 중복도 X.
  // ❹ 관찰(owned:false) 핀은 손절/목표/이탈 억제 — 레벨 도달을 알고 싶으면 🔔 내 가격 알림 이용.
  // 🌙 09-23 공용 함수로 분리 — 낮 경로와 저녁장 경로가 같은 규칙을 쓴다(저녁엔 '평균가 아래로'만 쉼).
  const pinLevelAlerts = (it: DaytradeItem) => {
    if (!(pinned.includes(it.ticker) && plans[it.ticker]?.owned !== false)) return;
    const plan = plans[it.ticker];
    if (!plan) return;
    const fk = (k: string) => `${it.ticker}|${k}`;
    if (it.price <= plan.stop) { if (!_pinAlerted[fk('stop')]) { _pinAlerted[fk('stop')] = now; fresh.push(mk(it, 'stop', layerMsg('손절선 도달', `미리 정해둔 손절선 ${plan.stop.toLocaleString('ko-KR')}원에 닿았어요 — 손절을 실행할지 판단하세요.`))); } }
    else delete _pinAlerted[fk('stop')];
    if (it.price >= plan.target) { if (!_pinAlerted[fk('target')]) { _pinAlerted[fk('target')] = now; fresh.push(mk(it, 'target', layerMsg('목표가 도달', `미리 정해둔 목표가 ${plan.target.toLocaleString('ko-KR')}원에 닿았어요 — 이익을 실현할지 판단하세요.`))); } }
    else delete _pinAlerted[fk('target')];
    if (it.vwap && !_evening) {   // 🌙 저녁장엔 '평균가 아래로' 쉼(위 _evening 주석)
      // ❸ 히스테리시스 — VWAP ±0.2% 밴드 + 15분 쿨다운. 평균가 주변 맴돌 때 크로스마다 재울림(플래핑) 방지.
      if (it.price < it.vwap * 0.998) { const lb = _pinAlerted[fk('breakdown')]; if (!lb || now - lb > 15 * 60 * 1000) { _pinAlerted[fk('breakdown')] = now; fresh.push(mk(it, 'breakdown', layerMsg('평균가 아래로', '오늘 평균가 아래로 내려오며 상승 흐름이 약해졌어요.'))); } }
      else if (it.price > it.vwap * 1.002) delete _pinAlerted[fk('breakdown')];
    }
  };
  // 🛡️ #1 트레일링 스탑 제안 — 목표 절반 도달 + 손절이 아직 본전 아래면 "손절을 본전으로"(이긴 자리가 지는 자리 되는 것 차단). 보유 핀만.
  const pinHalfTarget = (it: DaytradeItem) => {
    if (!(pinned.includes(it.ticker) && plans[it.ticker]?.owned !== false)) return;
    const plan = plans[it.ticker];
    if (plan && plan.target > plan.entry) {
      const half = plan.entry + (plan.target - plan.entry) * 0.5;
      const beKey = `${it.ticker}|beraise`;
      if (it.price >= half && plan.stop < plan.entry && !_pinAlerted[beKey]) {
        _pinAlerted[beKey] = now;
        const gain = ((it.price / plan.entry - 1) * 100).toFixed(1);
        fresh.push(mk(it, 'trail', layerMsg('목표 절반 도달', `목표의 절반까지 왔어요(+${gain}%) — 손절선을 본전 ${plan.entry.toLocaleString('ko-KR')}원으로 올리면 이익을 지킬 수 있어요.`)));
      }
    }
  };
  for (const it of items) {
    if (!it.live) continue;
    // 🌙 09-23 저녁장(16~20시)엔 핀 손절·목표·목표 절반만 본다 — 급등 시작·고점 반전·매수 자리·종목 큰손·
    //   이익 되돌림 주의·오래 제자리 등 **나머지 감지는 전부 건너뛴다**(감시 종목도 마찬가지). 이유는 위 _evening 주석.
    if (_evening) { pinHalfTarget(it); pinLevelAlerts(it); continue; }
    const grade = rowGrade(it);
    const above = !!(it.vwap && it.price > it.vwap);
    const strength = it.strength ?? 0;
    const p = prev[it.ticker];
    // 🤫 침묵 감지 버퍼 — 정적(변동 수축+수급 물밑 우상향)용 시계열(≤12분). 수급 없으면 sup=0(그런 종목은 어차피 장전 안 됨).
    const qh = _quietHist[it.ticker] || (_quietHist[it.ticker] = []);
    qh.push({ t: now, price: it.price, amt: it.amount ?? 0, sup: (it.foreign_est ?? 0) + (it.inst_est ?? 0) });
    while (qh.length > 1 && now - qh[0].t > 12 * 60 * 1000) qh.shift();
    // 엔진이 본 장중 고점 — 이제 모든 종목 추적(관찰 종목 '꺾임' 감지 + 핀 트레일링 공용)
    const peak = Math.max(p?.peak ?? 0, it.price);
    // 🚀 강세 주도주 상태 — 돈 몰림(거래대금 5천억+ or 거래량급증 300%+) + 강도 115+ + 오름세 초입(+1.5%~, 아직 크게 안 감)
    const amtEok = (it.amount ?? 0) / 1e8;
    const isLeader = (amtEok >= 5000 || (it.volume_surge ?? 0) >= 300) && it.change_pct >= 1.5 && strength >= 115;
    // 🐜 ③ 개미 단독 랠리 경고 — 오르는데(≥+1%) 개미만 순매수(≥50억)하고 외/기 둘 다 매도면 '물량받이' 경계.
    //   개미 순매수는 통계적으로 역방향(큰손이 개미에 넘기는 중)일 때가 잦아, 매수형 알림에 꼬리표로만 붙인다.
    const _antRally = it.change_pct >= 1 && (it.retail_est ?? 0) >= 50 && (it.foreign_est ?? 0) < 0 && (it.inst_est ?? 0) < 0;
    const antTag = _antRally ? ' 다만 개인 혼자 사들이는 중이라 물량받이가 될 수 있어요.' : '';
    // 🧾 ⑤ 부가 수급 — 가격성 알림(급등락 초입·재가속 등)의 보조 줄(외국인·기관·개인 풀네임). '왜 움직였나'를 봉합.
    const supTag = auxFlow(it);
    // 🐋 종목 큰손(②) + 🐜 개미 — 증분 방식: 기준선 대비 ±100/±300 대량. 기준선 이동으로 중복·새로고침 재울림 차단.
    //   외/기가 움직이면 반대편 개미는 '손바뀜' 태그로 병합(중복 억제), 외/기 조용한데 개미만 크면 단독 알림(신뢰 공백 해결). ⚠️ 하루 4~6회 갱신 장중 추정.
    // ① 몸집 비례 임계 — 대형주(거래대금 조 단위)에서 100억 고정 잣대는 잔물결마다 울림 → 거래대금 1.5%(최소 100억)로.
    //   + 종목·주체별 4분 쿨다운(단 대량=임계 3배는 관통). 개미(잔차)는 잔가지 많아 최소 300억.
    const _amtEok2 = (it.amount ?? 0) / 1e8;
    const _flowBar = Math.max(100, _amtEok2 * 0.015);
    const _flowBig = Math.max(300, _amtEok2 * 0.045);
    type _Fire = { src: string; buy: boolean; delta: number; val: number; big: boolean; turnTag: string };
    const _flowFire = (src: string, val: number | undefined, minBar = _flowBar): _Fire | null => {
      if (val == null) return null;
      const bkey = `${it.ticker}|${src}`;
      if (_stockFlowBase[bkey] === undefined) { _stockFlowBase[bkey] = val; return null; }   // 첫 관측은 기준선만
      const delta = val - _stockFlowBase[bkey];
      if (Math.abs(delta) < minBar) return null;   // 몸집 비례 임계 미만 침묵(기준선 보존→누적)
      const big = Math.abs(delta) >= _flowBig;
      if (!big && now - (_stockFlowFired[bkey] ?? 0) < 4 * 60 * 1000) return null;   // 4분 쿨다운(대량은 관통)
      _stockFlowBase[bkey] = val; _stockFlowFired[bkey] = now;
      const buy = delta > 0;
      const turn = Math.abs(val) >= 100 && (delta > 0) !== (val >= 0);
      return { src, buy, delta, val, big, turnTag: turn ? (buy ? ' 팔다가 사는 쪽으로 돌아섰어요.' : ' 사다가 파는 쪽으로 돌아섰어요.') : '' };
    };
    // ③ 이관 2단계 — 종목 큰손/개미 '울림'는 서버(market_events)로 완전 이관(브라우저 닫혀도 커버).
    //   기준선 계산은 클라도 유지(서버 장애 시 폴백 복원이 게이트 하나로 가능하게).
    const _SRV_FLOW = true;
    const _instFires = [_flowFire('외국인', it.foreign_est), _flowFire('기관', it.inst_est)].filter((f): f is _Fire => !!f);
    const _antFire = _flowFire('개미', it.retail_est, Math.max(300, _amtEok2 * 0.015));
    const _dom = _instFires.slice().sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))[0];   // 지배적 큰손(증분 큰 쪽)
    for (const f of (_SRV_FLOW ? [] : _instFires)) {
      // 반대편 개미가 같은 틱에 크게 움직였으면 지배적 큰손 줄에 '손바뀜'으로 병합(별도 개미 줄 억제 → 중복 없이 더 좋은 정보)
      const swap = (f === _dom && _antFire && _antFire.buy !== f.buy)
        ? ` 반대로 개인은 ${fmtEok(_antFire.delta)} ${_antFire.buy ? '받아내는' : '던지는'} 중이에요(손바뀜).` : '';
      // ④ 반대편 큰손이 훨씬 크면 병기 — "기관 담는 중"만 보고 낙관 오독 방지(그 순간 외국인은 대량 이탈 중일 수 있음).
      const _other = f.src === '외국인' ? it.inst_est : it.foreign_est;
      const _otherSrc = f.src === '외국인' ? '기관' : '외국인';
      const counterTag = (_other != null && (_other < 0) === f.buy && Math.abs(_other) >= Math.abs(f.val) * 2)
        ? ` 다만 ${_otherSrc}은 ${fmtEok(_other)} ${_other < 0 ? '빠져나가는' : '들어오는'} 중이라 힘이 부족할 수 있어요.` : '';
      const _sfHead = `${f.src} ${f.big ? '대량 ' : ''}${f.buy ? '매수' : '매도'}`;
      const _sfBody = `${f.src}이 이 종목을 ${f.big ? '대량으로 ' : ''}${f.buy ? '사들이고' : '팔고'} 있어요 — 방금 ${fmtEok(f.delta)}, 오늘 누적 ${fmtEok(f.val)}(장중 추정)이에요.`;
      fresh.push(mk(it, 'stockflow', layerMsg(_sfHead, _sfBody + f.turnTag + swap + counterTag + earlyGrace), false));
      logAlert('stockflow', it.name, it.ticker, '', it.price, f.buy ? 1 : -1, legLag(it, f.buy ? 1 : -1));
    }
    // 외국인·기관 조용한데 개인만 크게 움직임 → 지금까지 침묵하던 '개인 주도 변동'을 드러내는 단독 알림(신뢰 공백 해결)
    if (!_SRV_FLOW && _antFire && _instFires.length === 0) {
      const _afHead = `개인 ${_antFire.big ? '대량 ' : ''}${_antFire.buy ? '매수' : '매도'}`;
      const _afBody = `외국인·기관이 조용한 가운데 개인 혼자 ${_antFire.big ? '대량으로 ' : ''}${_antFire.buy ? '사들이고' : '팔고'} 있어요 — 방금 ${fmtEok(_antFire.delta)}예요(개인 물량은 잔차 추정).`;
      fresh.push(mk(it, 'antflow', layerMsg(_afHead, _afBody + _antFire.turnTag + earlyGrace), false, _antFire.buy ? 1 : -1));
      logAlert('antflow', it.name, it.ticker, '', it.price, _antFire.buy ? 1 : -1, legLag(it, _antFire.buy ? 1 : -1));
    }
    // 🧭 수급 구도 경보(②③) — 감시(👁)·핀(📌) 종목이 위태로운 구도(🚨물량받이·🏃큰손이탈)로 전환되면 경보(사운드).
    //   조각 알림(주체별)이 못 주는 '판의 그림'. 벤인 구도(쌍끌이 등)는 배지로만(스팸 방지), 위험 전환·해소만 소리. 15분 쿨다운.
    const _stance = stanceOf(it);
    if (p && (pinned.includes(it.ticker) || getWatch().includes(it.ticker))
        && _stance && _stance.key !== p.stance && now - (_stanceFired[it.ticker] ?? 0) > 15 * 60 * 1000) {
      const wasWarn = p.stance === 'unload' || p.stance === 'exit';
      if (_stance.warn || wasWarn) {   // 위험 진입 or 위험 해소만 알림(벤인↔벤인 전환은 침묵)
        _stanceFired[it.ticker] = now;
        const prevLabel = STANCE_LABEL[p.stance ?? ''] || '중립';
        // 🔪 오늘 장 성격 거부권(07-08 하루 복기: 패닉일 stance 33%) — 코스피 −2.5%↓ 패닉일엔 피드 침묵, 채점(logAlert)은 계속
        if (!(_kospiNow != null && _kospiNow <= -2.5)) {
          fresh.push(mk(it, 'stance', layerMsg(`${_stance.label} 구도로 바뀜`, `${_stance.hint} (${prevLabel}에서 바뀌었어요).${earlyGrace}`), false, _stance.warn ? -1 : 1));
        }
        logAlert('stance', it.name, it.ticker, '', it.price, _stance.warn ? -1 : 1, legLag(it, _stance.warn ? -1 : 1));
      }
    }
    if (p) {
      // 직전 폴링(~30초) 대비 가격 속도 + 체결강도 변화 — leader(초입)·ignition 공용
      const velo = (p.price ?? 0) > 0 ? (it.price / p.price - 1) * 100 : 0;
      const dStr = strength - (p.strength ?? strength);
      // #12 보유 관점 필터 — 실제 보유(핀+owned) 종목엔 '살 자리'·'곧 익을지도' 억제(청산형만 의미).
      //   ❹ 관찰(owned:false) 핀은 억제 안 함 → 지켜보는 종목의 진입 신호를 다시 받음(관찰의 목적).
      const owned = pinned.includes(it.ticker) && plans[it.ticker]?.owned !== false;
      // #8 종목 큰손 정합 게이트 — 시장 배경이 좋아도 '이 종목' 큰손(외+기)이 파는 중이면 매수 함정. (수급은 억 단위)
      const stockBig = (it.foreign_est ?? 0) + (it.inst_est ?? 0);
      const bigExitTag = stockBig <= -50 ? ` 다만 이 종목은 외국인·기관이 ${fmtEok(stockBig)} 빠져나가는 중이에요.` : '';
      // ✨ 살 자리 — 시장 배경 우호면 ★확신
      if (!owned && grade === '진입적기' && p.grade !== '진입적기') {
        const star = verdict === '우호' && !bigExitTag;   // 종목 큰손 이탈 중이면 ★확신 강등
        // 🅑 중립장 디레이트 — 지금 살 만함 신호가 중립장에서 부진했던 경험 반영. 문구로 정직하게(하드차단 아님, 피드는 유지).
        const neutralTag = verdict === '중립' ? ' 지금은 중립장이라 눌림을 한 번 더 확인하는 게 좋아요.' : '';
        // 🧲 #5 재진입 — 오늘 이미 살 자리였다가 이탈 후(≥15분) 다시 정렬됨 = '두 번째 눌림'(놓친 기회 회수).
        const prevEntry = _entryTs[it.ticker];
        const reentry = prevEntry !== undefined && now - prevEntry >= 15 * 60 * 1000;
        _entryTs[it.ticker] = now;
        const _eHead = reentry ? '다시 온 눌림목 자리' : '눌림목 매수 자리';
        const _eBody = (reentry ? '아까 지나갔던 눌림목 자리에 다시 정렬됐어요 — 놓쳤어도 다시 볼 만한 자리예요.'
          : star ? '눌림목 매수 후보 자리가 나왔고, 시장 배경도 사기 좋은 쪽이에요.'
          : '눌림목 매수 후보 자리가 나왔어요.') + chGate(true) + antTag + neutralTag + bigExitTag;
        fresh.push(mk(it, 'entry', layerMsg(_eHead, _eBody), star, 1));
      }
      // 🌡️ 아깝게 빗나감 — 정렬점수가 지금 살 만함(80) 직전 75~79에 '막' 진입(직전<75). 추격 아닌 예열. 20분 쿨다운.
      const ps = it.pullback_score ?? 0;
      if (!owned && ps >= 75 && ps < 80 && (p.pscore ?? 0) < 75 && grade !== '진입적기' && now - (_nearFired[it.ticker] ?? 0) > 20 * 60 * 1000) {
        _nearFired[it.ticker] = now;
        fresh.push(mk(it, 'nearmiss', layerMsg('매수 자리 임박', `매수 자리 바로 직전까지 왔어요 — 아직 자리가 완성되진 않았으니 미리 차트를 봐두세요.${chGate(true)}`), false, 1));
        logAlert('nearmiss', it.name, it.ticker, '', it.price, 1, legLag(it, 1));   // 07-07 전수감사: 등록만 있고 무채점이던 유령
      }
      // 🧯 매도 소진 — 급락 종목이 바닥 '다지기(3)/첫반등(4)'에 새로 진입(직전 <3). 저점 매수 골든타임.
      //   오늘 장 성격 게이트 미적용(하락장 전용 도구라 패닉일수록 필요). 가짜 바닥 리스크 → '1차만·손절 짧게' 명시. 20분 쿨다운.
      // ③ 서버 이관(알림개선) — floorset·capitulation은 서버 감지로 일원화(브라우저 닫혀도 커버). 클라 울림 차단.
      if (false && (it.bottom_rank ?? 0) >= 3 && (p.brank ?? 0) < 3 && now - (_floorFired[it.ticker] ?? 0) > 20 * 60 * 1000) {
        _floorFired[it.ticker] = now;
        fresh.push(mk(it, 'floorset', layerMsg('매도 물량 소진 중', `급락하던 종목의 매도 물량이 소진되며 바닥을 다지는 모습이에요(오늘 ${it.change_pct.toFixed(1)}%) — 첫 진입만, 손절은 짧게 잡으세요.`), false, 1));
        logAlert('floorset', it.name, it.ticker, '', it.price, 1, legLag(it, 1));
      }
      // 🤝 개미 투항(④) — 하락 중(≤−2%) 개미가 대량 투매(≤−100억)하고 외/기가 순매수(≥+100억)로 받으면 바닥 근처 고전 패턴.
      //   낙폭과대(📉)·바닥 4단계와 시너지. 저점 매수 도구라 오늘 장 성격 게이트 미적용. 20분 쿨다운. 잔차 추정이라 '추정' 명시.
      if (false && it.change_pct <= -2 && (it.retail_est ?? 0) <= -100 && ((it.foreign_est ?? 0) + (it.inst_est ?? 0)) >= 100
          && now - (_capFired[it.ticker] ?? 0) > 20 * 60 * 1000) {
        _capFired[it.ticker] = now;
        const bigHand = fmtEok((it.foreign_est ?? 0) + (it.inst_est ?? 0));
        fresh.push(mk(it, 'capitulation', layerMsg('개인 투항 매물', `개인이 ${fmtEok(it.retail_est ?? 0)} 던지는 걸 외국인·기관이 ${bigHand} 받아내는 중이에요(오늘 ${it.change_pct.toFixed(1)}%) — 바닥 근처 신호일 수 있어요(개인 물량은 추정).${earlyGrace}`), false, 1));
        logAlert('capitulation', it.name, it.ticker, '', it.price, 1, legLag(it, 1));
      }
      // 🔻 고점 반전/꺾임 (관찰 종목 포함, B안=더 예민) — 두 갈래:
      //  ① 고점 대비 −2.5%+ 빠지고 매수세 식음(고점이 평균가 위였던 '올랐던' 종목만, 아직 천장 근처)
      //  ② 평균가(VWAP) 위→아래로 꺾임(아직 양봉일 때). 시장 역풍이면 ★확신.
      const dropPct = peak > 0 ? (peak - it.price) / peak * 100 : 0;
      const wasUp = peak > (it.vwap ?? peak);          // 고점이 평균가 위 = 한 번 올랐던 종목
      const rollDrop = dropPct >= 2.5 && strength <= 100 && wasUp && it.change_pct > -3;
      const rollVwap = p.above && !above && (p.strength ?? 0) >= 100 && it.change_pct > 0;
      if ((rollDrop || rollVwap) && now - (_topFired[it.ticker] ?? 0) > 15 * 60 * 1000) {
        _topFired[it.ticker] = now;
        const star = verdict === '역풍';
        const _trBody = rollDrop ? `고점 대비 ${dropPct.toFixed(1)}% 밀리며 매수세가 식고 있어요.` : '오늘 평균가를 깨고 내려오며 매수세가 꺾였어요.';
        fresh.push(mk(it, 'topreversal', layerMsg('고점 찍고 꺾이는 중', _trBody + (star ? ' 시장도 역풍이라 물량을 나눠 파는 흐름일 수 있으니 따라 사는 건 조심하세요.' : ' 반전일 수 있어 따라 사는 건 위험해요.')), star, -1));
        logAlert('topreversal', it.name, it.ticker, '', it.price, -1, legLag(it, -1));   // 07-07 전수감사: 등록만 있고 무채점이던 유령
      }
      // 🚀 강세 주도주 — 돈 몰리는 종목이 '오름세 초입'에 들 때 1회(선행: +3% 다 오른 뒤가 아니라 +1.5%~ 아직 오르는 중)
      if (isLeader && !p.leader && it.change_pct < 4.5 && velo >= 0.2 && now - (_leaderFired[it.ticker] ?? 0) > 20 * 60 * 1000) {
        _leaderFired[it.ticker] = now;
        const amtStr = amtEok >= 10000 ? `${(amtEok / 10000).toFixed(1)}조` : `${Math.round(amtEok).toLocaleString('ko-KR')}억`;
        fresh.push(mk(it, 'leader', layerMsg('주도주로 오르는 중', `돈이 몰리며 오르기 시작했어요 — 거래대금 ${amtStr}, 오늘 +${it.change_pct.toFixed(1)}%예요.${chGate(true)}${antTag}`, supTag), false, 1));
        logAlert('leader', it.name, it.ticker, '', it.price, 1, legLag(it, 1));
      }
      // ③ 이관 2단계 분업 — 서버 빠른 레인(15초)이 전 종목 표준 임계 담당. 클라는 감시/핀의 '예민 임계'만 잔류(개인화).
      const _sensitive = pinned.includes(it.ticker) || _watchSet.has(it.ticker);
      // 🔥 급등/급락 초입(ignition) — '이미 간 것'이 아니라 '지금 붙는 중'을 가속으로 잡음(따라탈 타이밍).
      //   직전 폴링(~30초) 대비 가격 속도(velo) + 체결강도 급변(dStr) + 아직 초입(당일 이동폭 작음). 종목당 10분 1회.
      if (_sensitive && now - (_igniteFired[it.ticker] ?? 0) > 10 * 60 * 1000) {
        const _vb = _veloBar(it.ticker);   // ❷ 종목 체급별 이상가속 바(대형주 완화·잡주 현행 유지)
        // 급등 초입: 30초 이동이 자기 체급 바↑ · 체결강도 118+ 이고 방금 +8↑ 급등 · VWAP 위 · 아직 당일 +2.5% 미만
        if (velo >= _vb && strength >= 118 && dStr >= 8 && above && it.change_pct < 2.5) {
          _igniteFired[it.ticker] = now;
          const star = verdict === '우호';
          fresh.push(mk(it, 'ignition', layerMsg('빠르게 오르기 시작', `방금 매수가 빠르게 붙고 있어요 — 30초 만에 +${velo.toFixed(1)}%, 오늘 ${it.change_pct >= 0 ? '+' : ''}${it.change_pct.toFixed(1)}%예요.${star ? ' 시장 배경도 사기 좋은 쪽이에요.' : ''}${chGate(true)}${antTag}`, supTag), star, 1));
          logAlert('ignition', it.name, it.ticker, '', it.price, 1, legLag(it, 1));
        }
        // 급락 초입: 30초 하락이 자기 체급 바↑ · 체결강도 82− 이고 방금 −8↓ · VWAP 아래 · 아직 당일 −2.5% 초과
        else if (velo <= -_vb && strength <= 82 && dStr <= -8 && !above && it.change_pct > -2.5) {
          _igniteFired[it.ticker] = now;
          const star = verdict === '역풍';
          fresh.push(mk(it, 'ignition', layerMsg('빠르게 내리기 시작', `방금 매도가 빠르게 붙고 있어요 — 30초 만에 ${velo.toFixed(1)}%, 오늘 ${it.change_pct.toFixed(1)}%예요.${star ? ' 시장 배경도 파는 쪽이에요.' : ''}`, supTag), star, -1));
          logAlert('ignition', it.name, it.ticker, '', it.price, -1, legLag(it, -1));
        }
      }
      // 🤫 이상하게 조용함 알림(침묵 감지) — '소란 뒤 정적 + 물밑 매집' 성립 시 🌀장전(조용히 배지만), 이후 첫 미동에 격발(푸시). 거짓 경보 방지.
      const qwin = _quietHist[it.ticker] || [];
      const qspan = qwin.length ? (now - qwin[0].t) / 60000 : 0;
      const qarmed = _quietArmed[it.ticker];
      if (qarmed && now - qarmed.t > 60 * 60 * 1000) { delete _quietArmed[it.ticker]; _emit(); }   // 불발 만료(60분 내 미동 없음)
      else if (qarmed) {
        if (dStr >= 6 || velo >= 0.3) {   // 격발 — 장전 종목의 첫 미동(체결강도 급변 or 30초 가격 속도)
          delete _quietArmed[it.ticker]; _quietFired[it.ticker] = now;
          const supUp = qwin.length ? qwin[qwin.length - 1].sup - qwin[0].sup : 0;
          const star = verdict === '우호';
          fresh.push(mk(it, 'quiet', layerMsg('조용하다 방금 꿈틀', `${qspan.toFixed(0)}분 동안 숨죽이던 종목이 방금 움직이기 시작했어요 — 그 사이 큰손이 조용히 ${Math.round(supUp)}억을 사 모았어요.${star ? ' 시장 배경도 사기 좋은 쪽이에요.' : ''}${chGate(true)}${antTag}`, supTag), star, 1));
          logAlert('quiet', it.name, it.ticker, '', it.price, 1, legLag(it, 1));
        }
      } else if (qspan >= 6 && qwin.length >= 6 && now - (_quietFired[it.ticker] ?? 0) > 60 * 60 * 1000) {
        // 장전 판정 — 변동 수축 + VWAP 착붙 + 수급 물밑 우상향 + 거래 마름 + 최소 체력 + 과열 아님 + 시장 동반정적 아님
        const prices = qwin.map((q) => q.price);
        const range = it.vwap ? (Math.max(...prices) - Math.min(...prices)) / it.vwap * 100 : 99;
        const supUp = qwin[qwin.length - 1].sup - qwin[0].sup;
        const vwapHug = it.vwap ? Math.abs(it.price / it.vwap - 1) * 100 : 99;
        const mid = Math.floor(qwin.length / 2);
        const intv = (a: typeof qwin) => (a.length > 1 ? a[a.length - 1].amt - a[0].amt : 0);
        const drying = intv(qwin.slice(mid)) < intv(qwin.slice(0, mid + 1)) * 0.9;
        if (range < 0.7 && vwapHug < 0.5 && supUp >= 30 && drying && (it.amount ?? 0) >= 3e10 && it.change_pct > -3 && it.change_pct < 4 && !_lull) {
          _quietArmed[it.ticker] = { t: now, name: names[it.ticker] || it.name };   // 🌀 장전 — 알림·푸시 없이 배지만
          _emit();
        }
      }
      // 🎢 재가속 — 최근 5분/10분 다시 빠르게 움직이고 아직 진행 중. 🔥초입(±2.5% 미만)이 못 잡는 사각지대.
      //   ❶ 입장권: '당일 등락률 ±2.5%' OR '당일 고저 스윙폭'(감시2.5·일반4.0). 0을 가로지르는 V자(제자리 대진폭)도 포착.
      const _swingPct = (it.high && it.low && it.low > 0) ? (it.high - it.low) / it.price * 100 : 0;
      if (_sensitive && (Math.abs(it.change_pct) >= 2.5 || _swingPct >= 2.5)) {   // 서버가 전 종목 표준 담당 — 클라는 예민판만
        const qh2 = _quietHist[it.ticker] || [];
        const priceAgo = (mins: number): number | null => {
          const tgt = now - mins * 60000; let best: number | null = null;
          for (const q of qh2) { if (q.t <= tgt + 20000) best = q.price; }
          return best;
        };
        const p5 = priceAgo(5), p10 = priceAgo(10), p1 = priceAgo(1.5);
        if (p5 && p10 && p1) {
          const m5 = (it.price / p5 - 1) * 100, m10 = (it.price / p10 - 1) * 100;
          const hit5 = Math.abs(m5) >= (_sensitive ? 1.0 : 1.5);
          const hit10 = Math.abs(m10) >= (_sensitive ? 1.3 : 1.8);
          if (hit5 || hit10) {
            const mv = hit5 ? m5 : m10;
            const dn = mv < 0;
            if ((dn && it.price <= p1 * 0.999) || (!dn && it.price >= p1 * 1.001)) {   // 최근 ~1.5분 같은 방향 진행 중(완만한 출혈도 포착, 뒷북 방지)
              const akey = `${it.ticker}|${dn ? 'dn' : 'up'}`;
              let anchor: number | undefined = _surgeAnchor[akey];
              if (anchor !== undefined) {   // 다리 종료 인지(07-08): 반대로 1.5%+ 되돌렸으면 새 다리
                const _ret = (it.price / anchor - 1) * 100;
                if ((dn && _ret > 1.5) || (!dn && _ret < -1.5)) { delete _surgeAnchor[akey]; anchor = undefined; }
              }
              const newLeg = anchor === undefined || Math.abs(it.price / anchor - 1) * 100 >= 1.5;   // 같은 다리 재울림 차단, 새 다리만
              if (now - (_surgeFired[akey] ?? 0) > 10 * 60 * 1000 && newLeg && (_surgeCount[akey] ?? 0) < 3) {
                _surgeFired[akey] = now; _surgeAnchor[akey] = it.price; _surgeCount[akey] = (_surgeCount[akey] ?? 0) + 1;
                const win = hit5 ? `5분 ${m5 >= 0 ? '+' : ''}${m5.toFixed(1)}%` : `10분 ${m10 >= 0 ? '+' : ''}${m10.toFixed(1)}%`;
                const todayPct = `${it.change_pct >= 0 ? '+' : ''}${it.change_pct.toFixed(1)}%`;
                const accel = (it.change_pct < 0) === dn;   // 당일 방향 = 급변 방향 → 가속 / 반대 → 되돌림
                const _sgHead = accel ? (dn ? '다시 빠르게 내리는 중' : '다시 빠르게 오르는 중')
                  : (dn ? '오르던 게 되밀리는 중' : '빠지던 게 되돌리는 중');
                const _sgBody = accel
                  ? (dn ? `이미 오늘 ${todayPct}인데 최근 ${win} 더 내리며 낙폭을 키우고 있어요.`
                        : `이미 오늘 ${todayPct}인데 최근 ${win} 더 오르며 다시 속도가 붙었어요.`)
                  : (dn ? `오늘 ${todayPct}까지 올랐다가 최근 ${win} 반납하며 되밀리고 있어요.`
                        : `오늘 ${todayPct}까지 빠졌다가 최근 ${win} 되돌리며 반등하고 있어요.`);
                const sdir = (accel === !dn) ? 1 : -1;   // 상승재가속·낙폭되돌림=+1(오름 기대) / 하락재가속·상승반납=−1
                fresh.push(mk(it, 'surge', layerMsg(_sgHead, _sgBody + chGate(sdir > 0), supTag), false, sdir));
                logAlert('surge', it.name, it.ticker, '', it.price, dn ? -1 : 1, legLag(it, dn ? -1 : 1));
              }
            }
          }
        }
      }
      // 🛡️ 트레일링·#1 본전올리기·#2 시간손절 — 보유(핀+owned)일 때만. ❹ 관찰 핀은 보유성 알림 억제.
      if (pinned.includes(it.ticker) && plans[it.ticker]?.owned !== false) {
        const plan = plans[it.ticker];
        if (plan && peak > plan.entry && it.price <= peak * 0.97 && it.price > plan.entry
            && now - (_trailFired[it.ticker] ?? 0) > 20 * 60 * 1000) {
          _trailFired[it.ticker] = now;
          const gain = ((it.price / plan.entry - 1) * 100).toFixed(1);
          fresh.push(mk(it, 'trail', layerMsg('이익 되돌림 주의', `고점 대비 3% 되돌렸어요 — 지금 수익은 +${gain}%예요. 이익을 굳힐지 살펴보세요.`)));
        }
        pinHalfTarget(it);   // 🛡️ #1 목표 절반 도달 → 손절 본전 올리기 제안(루프 위 공용 함수 — 저녁 경로도 같은 것)
        // ⏳ #2 시간 손절 — 핀 후 30분+인데 본전 ±0.5% 박스면 '죽은 자리'. 단타에서 시간=리스크(다른 기회 비용).
        if (_pinSince[it.ticker] === undefined) _pinSince[it.ticker] = now;
        const held = (now - _pinSince[it.ticker]) / 60000;
        if (plan && held >= 30 && Math.abs(it.price / plan.entry - 1) * 100 < 0.5
            && now - (_timestopFired[it.ticker] ?? 0) > 30 * 60 * 1000) {
          _timestopFired[it.ticker] = now;
          fresh.push(mk(it, 'timestop', layerMsg('오래 제자리', `${Math.round(held)}분째 거의 제자리예요(±0.5%) — 다른 기회로 자금을 돌리는 걸 생각해볼 때예요.`)));
        }
      } else if (_pinSince[it.ticker] !== undefined) {
        delete _pinSince[it.ticker]; delete _timestopFired[it.ticker];   // 핀 해제 시 시간손절 상태 리셋
      }
    }
    pinLevelAlerts(it);   // 📌 핀 손절·목표·평균가 이탈(루프 위 공용 함수 — 저녁 경로도 같은 것)
    prev[it.ticker] = { grade, price: it.price, above, strength, peak, leader: isLeader, pscore: it.pullback_score, brank: it.bottom_rank, stance: _stance?.key };
  }
  // 🌙 #4 오버나잇 체크 — 14:50~15:00에 핀(보유) 종목이 있으면 하루 1회 "오늘 안 팔면 갭 리스크" 리마인더.
  //   단타는 원칙상 당일 청산 — 어영부영 오버나잇 되는 사고를 마지막에 막는다.
  try {
    const kst = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Seoul' }));
    const kstMin = kst.getHours() * 60 + kst.getMinutes();
    const today = kst.toISOString().slice(0, 10);
    const _ownedPins = pinned.filter((tk) => plans[tk]?.owned !== false);   // ❹ 관찰 핀 제외(미보유는 오버나잇 무관)
    if (kstMin >= 890 && kstMin <= 900 && _overnightFired !== today && _ownedPins.length) {
      _overnightFired = today;
      const rows = _ownedPins.map((tk) => {
        const it = items.find((i) => i.ticker === tk);
        const plan = plans[tk];
        const nm = names[tk] || it?.name || tk;
        if (it && plan?.entry) { const g = (it.price / plan.entry - 1) * 100; return `${nm} ${g >= 0 ? '+' : ''}${g.toFixed(1)}%`; }
        return nm;
      });
      fresh.push({ id: `overnight-${today}`, time: new Date().toLocaleTimeString('ko-KR'),
        name: '마감 임박', ticker: '', kind: 'overnight', ts: Date.now(),
        msg: layerMsg('마감 전 보유 점검', `마감이 가까워요 — 보유 ${_ownedPins.length}종목(${rows.join(' · ')})을 오늘 정리할지 밤새 들고 갈지 정하세요. 안 팔면 내일 아침 갭이 리스크예요.`) });
    }
  } catch { /* noop */ }
  // 핀 해제된 종목 플래그 정리(재핀 시 재무장) + 영속화 — 새로고침에도 손절/목표 상태 유지
  for (const k of Object.keys(_pinAlerted)) { if (!pinned.includes(k.split('|')[0])) delete _pinAlerted[k]; }
  _persistPinAlerted();
  _persistFired();   // 🧯 울림 기록 저장 — 새로고침 재울림 방지
  addAlerts(fresh);
}
let _overnightFired = '';   // 🌙 #4 오버나잇 체크 하루 1회 가드(YYYY-MM-DD)
// 🧪 시험 전용 창구(09-23 저녁장 분기 회귀 — alerts.evening.test.ts). 화면 코드는 쓰지 않는다.
export const _detectStockForTest = detectStock;


// (legEok·큰손 스윙/국면/장성격 영속 헬퍼는 서버 이관으로 제거됨)

// ── 🅔 내 가격 알림(사용자가 직접 긋는 선) ─────────────────────────
// 감시 종목·지수(KOSPI/KOSDAQ)에 "이 가격 닿으면" 등록 → 엔진이 도달 시 1회 울림(+푸시). 기기 저장.
export interface PriceAlert { id: string; ticker: string; name: string; price: number; dir: 'above' | 'below'; fired?: boolean; }
const _PRICE = 'dt_price_alerts';
const _priceSubs = new Set<() => void>();
export function getPriceAlerts(): PriceAlert[] { return readJSON<PriceAlert[]>(_PRICE, []); }
function _savePriceAlerts(list: PriceAlert[]) {
  try { localStorage.setItem(_PRICE, JSON.stringify(list)); } catch { /* noop */ }
  _priceSubs.forEach((f) => f());
}
export function addPriceAlert(ticker: string, name: string, price: number, dir: 'above' | 'below'): void {
  const list = getPriceAlerts();
  list.push({ id: `${ticker || 'idx'}-${Date.now()}`, ticker, name, price, dir });
  _savePriceAlerts(list);
}
export function removePriceAlert(id: string): void { _savePriceAlerts(getPriceAlerts().filter((p) => p.id !== id)); }
export function usePriceAlerts(): PriceAlert[] {
  const [, bump] = useState(0);
  useEffect(() => { const u = () => bump((n) => n + 1); _priceSubs.add(u); return () => { _priceSubs.delete(u); }; }, []);
  return getPriceAlerts();
}
// 가격 도달 감지 — priceMap(ticker→현재가)으로 미울림 알림 검사. 종목은 scanTick, 지수는 bdTick이 호출.
function checkPriceAlerts(priceMap: Record<string, number>) {
  const list = getPriceAlerts();
  if (!list.length) return;
  const time = new Date().toLocaleTimeString('ko-KR');
  const fresh: DtAlert[] = [];
  let changed = false;
  for (const pa of list) {
    if (pa.fired) continue;
    const cur = priceMap[pa.ticker];
    if (cur == null) continue;
    const hit = pa.dir === 'above' ? cur >= pa.price : cur <= pa.price;
    if (!hit) continue;
    pa.fired = true; changed = true;
    fresh.push({ id: `price-${pa.id}-${Date.now()}`, time, name: pa.name, ticker: pa.ticker.length === 6 ? pa.ticker : '',
      kind: 'pricealert', px: cur, ts: Date.now(),
      msg: layerMsg('내 가격 도달', `${pa.name}이(가) 내가 정해둔 ${pa.price.toLocaleString('ko-KR')}원을 ${pa.dir === 'above' ? '위로 넘었어요' : '아래로 닿았어요'}(현재 ${Math.round(cur).toLocaleString('ko-KR')}원).`) });
  }
  if (changed) { _savePriceAlerts(list); addAlerts(fresh); }
}

// 핀(보유) 손절/목표/이탈 알림 1회 플래그 영속화 — 새로고침 순간 가격이 손절선을 넘어도 누락 없이 잡기 위함.
const _PINALERT = 'dt_pin_alerted';
function _restorePinAlerted(): Record<string, number> {
  try { const r = JSON.parse(localStorage.getItem(_PINALERT) || 'null'); if (r && r.date === _today() && r.f) return r.f; } catch { /* noop */ }
  return {};
}
let _pinAlerted: Record<string, number> = _restorePinAlerted();
function _persistPinAlerted() {
  try { localStorage.setItem(_PINALERT, JSON.stringify({ date: _today(), f: _pinAlerted })); } catch { /* noop */ }
}


// 🧯 종목 알림 울림 기록 영속화 — 새로고침 시 인메모리 트래커가 리셋돼 이미 뜬 🐋큰손·🔥초입·🚀주도주·🔻고점반전·🤫이상하게 조용함이
// 같은 조건에서 '재울림'하는 중복(사용자 리포트: 새로고침하면 알림이 쭉 와있음)을 막는다. 당일만 유지, 다음날 자동 초기화.
const _FIRED = 'dt_fired';
function _restoreFired() {
  try {
    const r = JSON.parse(localStorage.getItem(_FIRED) || 'null');
    if (r && r.date === _today()) {
      Object.assign(_stockFlowBase, r.stockFlow || {});
      Object.assign(_igniteFired, r.ignite || {});
      Object.assign(_leaderFired, r.leader || {});
      Object.assign(_topFired, r.top || {});
      Object.assign(_quietFired, r.quiet || {});
      Object.assign(_surgeFired, r.surgeF || {});
      Object.assign(_surgeAnchor, r.surgeA || {});
      Object.assign(_surgeCount, r.surgeC || {});
      Object.assign(_nearFired, r.near || {});
      Object.assign(_floorFired, r.floor || {});
      Object.assign(_capFired, r.cap || {});
      Object.assign(_stockFlowFired, r.sflow || {});
      Object.assign(_stanceFired, r.stance || {});
    }
  } catch { /* noop */ }
}
function _persistFired() {
  try { localStorage.setItem(_FIRED, JSON.stringify({ date: _today(), stockFlow: _stockFlowBase, ignite: _igniteFired, leader: _leaderFired, top: _topFired, quiet: _quietFired, surgeF: _surgeFired, surgeA: _surgeAnchor, surgeC: _surgeCount, near: _nearFired, floor: _floorFired, cap: _capFired, sflow: _stockFlowFired, stance: _stanceFired })); } catch { /* noop */ }
}

// 💥 시장 급변(#1) — 속도 기반: 최근 ~몇 분 지수 변화가 가파르면(빠르게 낙하/급등) 초기 경보(레벨/누적 아님).
// 히스토리·쿨다운은 인메모리(새로고침 시 ~1분 웜업). 종합판정과 무관하게 지수 자체 속도를 직접 경보.
const _idxHist: Record<string, { t: number; pct: number }[]> = {};   // 시장별 최근 (시각, 등락률) 포인트(≤4분)
// 🧭 최근 창(≤4분) 지수 방향 — 해석 레이어(interpret.ts)가 '지수 오르는 중/내리는 중'을 판단하는 데 사용. ±0.15%p 이상만 방향 인정(노이즈 컷).
export function idxTrend(mkey = 'KOSPI'): number {
  const h = _idxHist[mkey];
  if (!h || h.length < 2) return 0;
  const d = h[h.length - 1].pct - h[0].pct;
  return d >= 0.15 ? 1 : d <= -0.15 ? -1 : 0;
}
// (💥급변·🩹반등 클라 감지 삭제 07-07 — 서버 지수펄스 이관. _idxMoveFired/_revFired/_topMovers/_impactTag는
//  서버 쪽 대응물(market_events.pulse·_impact_tag)로 흡수. '주도 종목 꼬리표'는 서버 펄스에 미이식 — 후속 후보.)

// ⚠️ 큰손 스윙(flow)·순환매(rotation)·국면·장성격·합류는 서버(market_events.py)로 이관 — 기기별 기준선(localStorage)이
//    갈려 같은 시장인데 기기마다 다른 알림이 뜨던 문제 해결(mergeMarketEvents로 서버 목록 표시). 여기 남는 건 💥급변·🩹반등(분 단위 지수속도) 뿐.
//    이건 인메모리 히스토리라 기기간 이미 일관하고, 복귀 시 히스토리가 비어 자기방어(winMin<1.2 가드)돼 몰아치기 없음.
function detectFlow(markets: Record<string, { foreign: number | null; institution: number | null; pension?: number | null }> | undefined,
                    kospiPct?: number | null, kosdaqPct?: number | null, asOf?: string, impact?: FlowImpact | null) {
  // ⚡ 07-07 서버 이관: 💥급변·🩹반등/꺾임 울림은 서버 지수펄스(market_events.pulse, KIS 20초 폴)가 단일 소스.
  //   브라우저 닫혀도 동작 + 대형 급변은 잠금화면 푸시 + 이중 울림/이중 알림 기록 제거. 서버 이벤트는 mergeMarketEvents로 유입.
  //   여기는 _idxHist(≤4분)만 유지 — idxTrend(💡해석 '지수 오르는/내리는 중')의 재료.
  void markets; void asOf; void impact;
  const nowMs = Date.now();
  for (const [, mkey] of [['코스피', 'KOSPI'], ['코스닥', 'KOSDAQ']] as [string, string][]) {
    const pct = mkey === 'KOSPI' ? kospiPct : kosdaqPct;
    if (pct == null) continue;
    const hist = _idxHist[mkey] || (_idxHist[mkey] = []);
    hist.push({ t: nowMs, pct });
    while (hist.length > 1 && nowMs - hist[0].t > 4 * 60 * 1000) hist.shift();
  }
}

// 🌐 서버 시장 이벤트 머지 — 큰손·순환매·국면·장성격·합류를 서버 단일 소스에서 받아 피드에 통합(기기간 동일).
//   이미 본 id는 건너뛰고, 오래된 백필(2분 초과)은 조용히(소리 없이·푸시X) 추가해 '폰이 자던 사이' 이벤트도 진짜 시각으로 채움.
const _MEV_SEEN = 'dt_mev_seen';
function _mevSeen(): Set<string> {
  try { const r = JSON.parse(localStorage.getItem(_MEV_SEEN) || 'null'); if (r && r.date === _today()) return new Set<string>(r.ids); } catch { /* noop */ }
  return new Set<string>();
}
function _persistMevSeen(s: Set<string>) {
  try { localStorage.setItem(_MEV_SEEN, JSON.stringify({ date: _today(), ids: Array.from(s).slice(-500) })); } catch { /* noop */ }
}
async function mergeMarketEvents() {
  let events: MarketEvent[];
  try { events = await fetchDaytradeMarketEvents(); } catch { return; }
  ingestServerEvents(events);
}

// 📡 서버 이벤트 인제스트 — 폴(mergeMarketEvents)과 SSE 스트림이 공유(질적개선 A).
//   🦵 하이닉스 힘 감지 흐름 카드(07-24 3차) — 서버가 같은 방향 지속 시 고정 id로 upsert 재발행(cont=true=갱신본). 그 갱신본은
//     기존 seen 디둡('첫 건 유지')을 우회해 **knee에 한해 제자리·소리 없이 갱신**(updateKneeCards)한다. 다른 kind·새 흐름(cont 없음)은
//     기존 경로(seen 디둡 + 소리/푸시) 그대로 — 회귀 없음.
export function ingestServerEvents(events: MarketEvent[]) {
  if (!events.length) return;
  // 이중 방지 — 같은 종목·같은 종류가 10분 내 클라(예민판)에서 이미 울렸으면 서버판 드롭(감시/핀 분업의 접합부)
  const _nowD = Date.now();
  events = events.filter((e) => !e.ticker
    || !_alerts.some((a) => a.kind === e.kind && a.ticker === e.ticker && _nowD - (a.ts ?? 0) < 10 * 60 * 1000));
  if (!events.length) return;
  const toDt = (e: MarketEvent): DtAlert => ({ id: e.id, ts: e.ts, time: e.time, kind: e.kind as DtAlertKind, name: e.name, ticker: e.ticker || '', msg: e.msg, star: e.star, mkt: e.mkt, grp: e.grp, net: e.net,
    fdir: typeof e.dir === 'string' ? e.dir : undefined,               // flow류 'buy'|'sell'
    dir: typeof e.dir === 'number' ? e.dir : undefined,                // 지수펄스 ±1 — 방향 테두리·📊기대·✅❌ 판정에 필요(감사 07-07 버그①)
    jk: e.jk,   // 🧾 알림 기록 계정 — ⚡'조짐'(marketmove_fast) 검증 중 판정에 필요(서버 is_survivor와 같은 자)
    ep: e.ep,   // 🧵 에피소드
    verdict: e.verdict,
    url: e.url,   // 🗞️ news — 원문 링크
    kw: e.kw,     // 🗞️ news — 매치 키워드
    cont: e.cont,   // 🦵 knee 흐름 갱신본 표식
    ...(_driveNow != null && Date.now() - (e.ts ?? 0) <= 2 * 60 * 1000 ? { drv: _driveNow } : {}) });
  // 🦵 하이닉스 힘 감지 흐름 카드 갱신본(cont=true) — seen 우회·제자리 소리 없이 갱신. 나머지(새 흐름·타 kind)는 기존 경로.
  const kneeUpd = events.filter((e) => e.kind === 'knee' && e.cont === true);
  const rest = events.filter((e) => !(e.kind === 'knee' && e.cont === true));
  if (kneeUpd.length) updateKneeCards(kneeUpd.map(toDt));
  if (!rest.length) return;
  const seen = _mevSeen();
  const fresh = rest.filter((e) => e.id && !seen.has(e.id));
  if (!fresh.length) return;
  for (const e of fresh) seen.add(e.id);
  _persistMevSeen(seen);
  const now = Date.now();
  // 최근(≤2분) 이벤트만 '새 알림'으로(소리·푸시). 백필(오래된 것)은 소리 없이 피드에만 — 복귀 시 몰아치는 소리 방지.
  const recent = fresh.filter((e) => now - (e.ts ?? 0) <= 2 * 60 * 1000).map(toDt);
  const backfill = fresh.filter((e) => now - (e.ts ?? 0) > 2 * 60 * 1000).map(toDt);
  if (backfill.length) addAlerts(backfill, { silent: true });
  if (recent.length) addAlerts(recent);
}

// 🦵 하이닉스 힘 감지 흐름 카드 제자리 갱신 — 같은 id 카드의 내용(msg·방향·cont)만 최신화(위치 ts 유지), 없으면 조용히 추가.
//   ⚠️ 소리·OS알림·푸시 없음(notify/playChime 호출 안 함) — 새 흐름 카드만 addAlerts로 소리 켜짐. knee 전용(타 kind 디둡 불변).
export function updateKneeCards(ups: DtAlert[]) {
  let changed = false;
  for (const u of ups) {
    if (u.kind !== 'knee') continue;   // 안전 가드 — knee만 제자리 갱신
    const idx = _alerts.findIndex((a) => a.id === u.id);
    if (idx >= 0) {
      if (_alerts[idx].msg !== u.msg || _alerts[idx].cont !== u.cont) {
        _alerts[idx] = { ..._alerts[idx], msg: u.msg, dir: u.dir ?? _alerts[idx].dir, cont: u.cont };   // 위치(ts)·울릴 때각(time) 유지
        changed = true;
      }
    } else if (admitToFeed(u)) {   // 첫 카드를 놓친 경우(중간 합류) — 조용히 추가(검증 중)
      if (u.ts == null) u.ts = Date.now();
      _alerts = [u, ..._alerts].sort((a, b) => (b.ts ?? 0) - (a.ts ?? 0));
      changed = true;
    }
  }
  if (changed) { _persist(); _emit(); }
}

// 🎯 '탔어요' — 큰손 알림 보고 진입한 것을 기기에 표시. 그 주체 수급이 식으면(동력 소진) 한 번 알림(수급 출구).
//   fdir='buy'면 정점(peak) 대비 되팔기, 'sell'이면 저점 대비 되사기 감지. 데이터는 장중 잠정치라 여유(GAP)를 크게.
type Ride = { mkt: string; grp: string; net: number; fdir: string; name: string; ts: number; peak: number; gainBase: number; gainTs: number };
const _RODE = 'dt_rode';
let _rodeVer = 0;   // 구독 재렌더용
function getRides(): Record<string, Ride> {
  try { const r = JSON.parse(localStorage.getItem(_RODE) || 'null'); if (r && r.date === _today()) return r.rides || {}; } catch { /* noop */ }
  return {};
}
function _saveRides(rides: Record<string, Ride>) {
  try { localStorage.setItem(_RODE, JSON.stringify({ date: _today(), rides })); } catch { /* noop */ }
  _rodeVer++; window.dispatchEvent(new Event('dt-rode'));
}
export function isRidden(id: string): boolean { return !!getRides()[id]; }
export function toggleRide(a: DtAlert): boolean {
  // 큰손(flow) 알림만 탈 수 있음(주체·울림누적이 있어야 소진 감지 가능)
  if (!a.mkt || !a.grp || a.net == null || !a.fdir) return false;
  const rides = getRides();
  if (rides[a.id]) delete rides[a.id];
  else {
    // 같은 주체(시장+주체)는 하나만 — 새로 타면 이전 걸 자동 해제(같은 기관 흐름을 두 번 못 탐).
    for (const [id, r] of Object.entries(rides)) if (r.mkt === a.mkt && r.grp === a.grp) delete rides[id];
    if (Object.keys(rides).length >= 12) return false;   // 상한
    rides[a.id] = { mkt: a.mkt, grp: a.grp, net: a.net, fdir: a.fdir, name: a.name, ts: Date.now(), peak: a.net, gainBase: a.net, gainTs: 0 };
  }
  _saveRides(rides);
  return true;
}
export function useRides(): number {
  const [, force] = useState(0);
  useEffect(() => { const h = () => force((v) => v + 1); window.addEventListener('dt-rode', h); return () => window.removeEventListener('dt-rode', h); }, []);
  return _rodeVer;
}
// 매 bdTick(현재 markets 보유) — 탄 수급이 정점 대비 크게 되돌면 '동력 소진' 1회 알림 후 마크 해제.
function checkRides(markets: Record<string, { foreign: number | null; institution: number | null; pension?: number | null }> | undefined) {
  if (!markets) return;
  const rides = getRides();
  const ids = Object.keys(rides);
  if (!ids.length) return;
  const GAP = 1500;    // 정점 대비 1,500억 되돌면 소진(장중 잠정치 출렁임에 안 속게 크게)
  const GAIN = 1000;   // 직전 기준선 대비 1,000억 더 담/던지면 '증축'(흐름 건재) — 오름음
  const glabel: Record<string, string> = { foreign: '외국인', institution: '기관', pension: '연기금' };
  const now = Date.now();
  const fresh: DtAlert[] = [];
  let changed = false;
  for (const id of ids) {
    const r = rides[id];
    const cur = (markets[r.mkt] as Record<string, number | null> | undefined)?.[r.grp];
    if (cur == null) continue;
    const buy = r.fdir === 'buy';
    // 정점(방향 극값) 갱신
    const newPeak = buy ? Math.max(r.peak, cur) : Math.min(r.peak, cur);
    if (newPeak !== r.peak) { r.peak = newPeak; changed = true; }
    // 🎯 증축(ridegain) — 직전 기준선 대비 방향대로 GAIN만큼 더. 10분 쿨다운, 기준선 이동으로 같은 증분 재울림 차단.
    const grew = buy ? cur - r.gainBase >= GAIN : r.gainBase - cur >= GAIN;
    if (grew && now - r.gainTs > 10 * 60 * 1000) {
      const more = buy ? cur - r.gainBase : r.gainBase - cur;
      fresh.push({ id: `ridegain-${id}-${now}`, time: new Date().toLocaleTimeString('ko-KR'), name: r.name, ticker: '', kind: 'ridegain', ts: now,
        msg: layerMsg(`탄 ${glabel[r.grp] || r.grp} 흐름 건재`, `타신 ${glabel[r.grp] || r.grp} 흐름이 그 뒤로도 ${fmtEok(more).replace('+', '')} 더 ${buy ? '사들이며' : '팔며'} 이어지고 있어요.`) });
      r.gainBase = cur; r.gainTs = now; changed = true;
    }
    // 소진(ridefade) — 정점 대비 GAP만큼 되돌아옴. 새 정점에선 fade 조건이 거짓이라 gain과 동시울림 안 됨.
    const faded = buy ? cur <= r.peak - GAP : cur >= r.peak + GAP;
    if (faded) {
      fresh.push({ id: `ridefade-${id}-${now}`, time: new Date().toLocaleTimeString('ko-KR'), name: r.name, ticker: '', kind: 'ridefade', ts: now,
        msg: layerMsg(`탄 ${glabel[r.grp] || r.grp} 흐름 식는 중`, `타신 ${glabel[r.grp] || r.grp} ${buy ? '매수' : '매도'} 힘이 식고 있어요 — 정점 ${fmtEok(r.peak)}에서 현재 ${fmtEok(cur)}로 되돌렸어요. 이익을 지킬지 살펴보세요.`) });
      delete rides[id]; changed = true;
    }
  }
  if (changed) _saveRides(rides);
  if (fresh.length) addAlerts(fresh);
}

// ── 항상 떠 있는 엔진(App 최상위에서 1회 호출) ──────────────────
export function useAlertEngine() {
  const prevRef = useRef<Record<string, { grade: string; price: number; above?: boolean; strength?: number; peak?: number; leader?: boolean; pscore?: number; brank?: number; stance?: string }>>({});
  useEffect(() => {
    _restoreFired();   // 🧯 울림 기록 복원 — 새로고침해도 이미 뜬 종목 알림이 재울림하지 않게(중복 스팸 방지)
    if (isPushEnabled()) subscribeWebPush();   // 이미 켠 사용자 — 앱 열 때 구독 재확인(SW 갱신·구독 회전 대비)
    else unsubscribeWebPush();   // 🔕 마이그레이션 — 토글 OFF(또는 권한 회수)인데 구독 잔존 시 자동 해제(앱 한 번 열면 옛 구독 정리, 잠금화면 푸시 멈춤)
    // 핀(보유)·감시(👁)·🔔 중 하나라도 있으면 스캔 폴링 — 감시 종목의 알림도 챙기기 위함.
    const armed = () => readJSON<string[]>('dt_pinned', []).length > 0 || getWatch().length > 0 || isPushEnabled();
    const scanTick = async () => {
      if (!armed()) return;
      const pinned = readJSON<string[]>('dt_pinned', []);
      // 핀 + 👁 감시를 합쳐 스캔에 강제 포함(유니버스에 없어도 스냅샷+실시간 구독) → 그 종목들의 모든 알림이 뜸. 상한 8.
      const track = Array.from(new Set([...pinned, ...getWatch()])).slice(0, 8);
      try {
        const r = await fetchDaytradeScan('active', true, track);   // 거래량+거래대금 합집합·ETF제외 + 핀·감시 종목
        if (!r.kis_ready) return;
        detectStock(r.items, pinned, readJSON('dt_pin_plans', {}), readJSON('dt_pin_names', {}), prevRef.current);
        annotateOutcomes(r.items);   // 🅑 결과 꼬리표 갱신(울림 알림에 그 후 ±% 채움)
        const pm: Record<string, number> = {};
        for (const it of r.items) if (it.live && it.price) pm[it.ticker] = it.price;
        checkPriceAlerts(pm);   // 🔔 종목 가격 알림 검사
      } catch { /* noop */ }
    };
    const bdTick = async () => {
      // 🌐 큰손·국면·급변·순환매 = 시장 전체 신호 → 앱 열려 있으면 armed 아니어도 감지(핀·🔔 없이도 다 받게).
      //    핀 손절/목표(개인)·스캔은 scanTick에서 armed로만. 🔔(푸시)는 잠금화면(앱 닫힘)용 별개 — notify가 알아서 게이트.
      try {
        const d = await fetchDaytradeBackdrop();
        addPulse(d);   // 🫀 시장 펄스 시계열 누적(다른 탭에 있어도 흐름이 안 끊김)
        // ⚡ 국면(regime)·🌗 장성격(dayshift) 전환 알림은 서버(market_events)로 이관 — 기기간 통일.
        //    여기선 상태만 최신화(매수형 알림 게이트·확신★·가격알림용). 실제 전환 알림은 아래 mktEventTick가 서버에서 받아 표시.
        _lastVerdict = d.verdict;
        _lastCharacter = d.character ?? null;   // 🅐 매수형 알림 게이트용 최신 장 성격
        _kospiNow = d.kospi_pct ?? null;        // 🔪 오늘 장 성격 거부권 재료
        _driveNow = d.drive?.score ?? null;     // 🚀 보드 문맥 칩 재료
        const ch = d.character;
        // 🔔 지수(코스피/코스닥) 가격 알림 — character의 지수 레벨로 검사
        if (ch && (ch.kospi_index || ch.kosdaq_index)) {
          const m: Record<string, number> = {};
          if (ch.kospi_index) m['KOSPI'] = ch.kospi_index;
          if (ch.kosdaq_index) m['KOSDAQ'] = ch.kosdaq_index;
          checkPriceAlerts(m);
        }
        detectFlow(d.markets, d.kospi_pct, d.kosdaq_pct, d.as_of, d.impact);   // 💥급변·🩹반등(지수속도) — 클라 유지
        checkRides(d.markets);   // 🎯 탄 수급 소진 감지(현재 markets로)
        startEventStream();          // 📡 SSE 실시간 수신 시작(1회) — 이후 이벤트는 즉시 도착
        await mergeMarketEvents();   // 🌐 백필·SSE 유실 보험(30초 폴 유지 — 이중화)
      } catch { /* noop */ }
    };
    const viTick = async () => {
      if (!armed() || !isKindEnabled('vi')) return;   // VI는 기본 OFF — 켰을 때만 폴링
      try {
        const r = await fetchDaytradeScan('vi', false, []);
        if (!r.kis_ready) return;
        const time = new Date().toLocaleTimeString('ko-KR');
        const fresh: DtAlert[] = [];
        for (const it of r.items) {
          if (_viSeenDay !== _today()) { _viSeen.clear(); _viSeenDay = _today(); }
          if (_viSeen.has(it.ticker)) continue;
          _viSeen.add(it.ticker);
          const viSup = auxFlow(it);
          if (_viInit) fresh.push({ id: `vi-${it.ticker}-${Date.now()}`, time, name: it.name, ticker: it.ticker, kind: 'vi', msg: layerMsg('VI 발동', '변동성완화장치가 걸릴 만큼 가격이 급하게 움직였어요.', viSup) });
        }
        _viInit = true;   // 첫 폴링은 기존 발동분 기록만(알림 X), 이후 새 발동만 알림
        addAlerts(fresh);
      } catch { /* noop */ }
    };
    scanTick(); bdTick();
    const s = setInterval(scanTick, 15000);   // 종목 알림 — 15초(표 2초보다 느리지만 API 절제). 백그라운드 복귀 시 아래서 즉시 보정.
    const b = setInterval(bdTick, 30000);   // 07-09 60초→30초(1분 신선도 창의 해상도 — 서버는 state_bus 25초 캐시라 부담 없음)
    const v = setInterval(viTick, 45000);
    // 📱 백그라운드→포그라운드 복귀 시 즉시 갱신 — 모바일/탭전환 중엔 브라우저가 setInterval을 멈춰/늦춰 알림이 밀림.
    // 앱을 다시 보는 순간 바로 스캔해, 수동 새로고침 없이도 종목/시장 알림이 최신으로 올라오게 함(사용자 리포트 해결).
    const onVisible = () => { if (!document.hidden) { scanTick(); bdTick(); if (isKindEnabled('vi')) viTick(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(s); clearInterval(b); clearInterval(v); document.removeEventListener('visibilitychange', onVisible); };
  }, []);
}
